import React, { useState, useEffect, useCallback, useMemo, createContext, useContext, useRef } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  House, Wrench, Calculator, Clock, Users, Receipt, 
  CaretRight, CaretLeft, Check, X, Plus, Minus,
  WhatsappLogo, Phone, Envelope, MapPin, Calendar,
  MagnifyingGlass, Funnel, DotsThree, Trash, PencilSimple,
  Camera, Signature, Download, Share, QrCode,
  Gear, Lock, Bell, ChartBar, Money, Package,
  FileText, Warning, CheckCircle, Info, Star, Brain, Sparkle, CaretDown
} from "@phosphor-icons/react";
import "./App.css";
import { abrirWhatsApp } from "./utils/api";

// Nuevos componentes modulares
import { TarjetaDigital } from "./components/client/TarjetaDigital";
import { AsesorTecnicoIA } from "./components/client/AsesorTecnicoIA";
import { AsistenteFlotantePresupuesto } from "./components/client/AsistenteFlotantePresupuesto";
import { DiagnosticoInteligente } from "./components/client/DiagnosticoInteligente";
import { Catalogo } from "./components/client/Catalogo";
import { CatalogoCliente } from "./components/client/CatalogoCliente";
import { VisitaTecnica } from "./components/admin/VisitaTecnica";
import { GestoraAutomatica } from "./components/admin/GestoraAutomatica";
import { AsistenteObra } from "./components/admin/AsistenteObra";
import { Agenda } from "./components/admin/Agenda";
import { GestorMateriales } from "./components/admin/GestorMateriales";
import { EditorTrabajos } from "./components/admin/EditorTrabajos";
import { EditorPreguntas } from "./components/admin/EditorPreguntas";
import { PresupuestosPendientes } from "./components/admin/PresupuestosPendientes";
import { Proveedores } from "./components/admin/Proveedores";
import { Facturas } from "./components/admin/Facturas";
import { Garantias } from "./components/admin/Garantias";
import { CRMClientes } from "./components/admin/CRMClientes";
import { WhatsAppInbox } from "./components/admin/WhatsAppInbox";
import { MenuAdmin } from "./components/admin/MenuAdmin";
import { Configuracion } from "./components/admin/Configuracion";
import { Historial } from "./components/admin/Historial";
import { Informes } from "./components/admin/Informes";
import { Notificaciones } from "./components/admin/Notificaciones";
import { Formulas } from "./components/admin/Formulas";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// ==========================================
// CONTEXT & HELPERS
// ==========================================

const AppContext = createContext();
const useApp = () => useContext(AppContext);

const api = {
  get: async (url, token = null) => {
    const headers = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(`${API}${url}`, { headers });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
  post: async (url, data, token = null) => {
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(`${API}${url}`, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
  put: async (url, data, token = null) => {
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(`${API}${url}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
  delete: async (url, token = null) => {
    const headers = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(`${API}${url}`, { method: "DELETE", headers });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
};

// ==========================================
// COMPONENTES COMUNES
// ==========================================

const Toast = ({ message, type = "success", onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3500);
    return () => clearTimeout(timer);
  }, [onClose]);

  const icons = {
    success: <CheckCircle size={20} weight="fill" />,
    error: <Warning size={20} weight="fill" />,
    info: <Info size={20} weight="fill" />,
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      className={`toast toast-${type}`}
      data-testid="toast"
    >
      {icons[type]}
      <span>{message}</span>
    </motion.div>
  );
};

const Modal = ({ isOpen, onClose, title, children, footer }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="modal-backdrop"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-handle" />
          {title && (
            <div className="modal-header">
              <h3 className="text-h3">{title}</h3>
              <button onClick={onClose} className="btn-icon-sm btn-ghost">
                <X size={20} />
              </button>
            </div>
          )}
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const Button = ({ children, variant = "primary", size = "", className = "", icon, ...props }) => {
  return (
    <button
      className={`btn btn-${variant} ${size ? `btn-${size}` : ""} ${className}`}
      {...props}
    >
      {icon}
      {children}
    </button>
  );
};

const Input = ({ label, error, ...props }) => (
  <div className="input-group">
    {label && <label className="input-label">{label}</label>}
    <input className={`input ${error ? "input-error" : ""}`} {...props} />
    {error && <span className="text-tiny" style={{ color: "var(--error)" }}>{error}</span>}
  </div>
);

const Textarea = ({ label, ...props }) => (
  <div className="input-group">
    {label && <label className="input-label">{label}</label>}
    <textarea className="input textarea" {...props} />
  </div>
);

const Select = ({ label, options, ...props }) => (
  <div className="input-group">
    {label && <label className="input-label">{label}</label>}
    <select className="input select" {...props}>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

const Badge = ({ children, variant = "primary" }) => (
  <span className={`badge badge-${variant}`}>{children}</span>
);

const Chip = ({ children, active, onClick }) => (
  <button
    className={`chip chip-interactive ${active ? "chip-active" : ""}`}
    onClick={onClick}
  >
    {children}
  </button>
);

const EmptyState = ({ icon, title, description, action }) => (
  <div className="empty-state">
    <div className="empty-state-icon">{icon}</div>
    <div className="empty-state-title">{title}</div>
    <div className="empty-state-description">{description}</div>
    {action && <div style={{ marginTop: 16 }}>{action}</div>}
  </div>
);

const Loading = () => (
  <div className="empty-state">
    <div className="animate-spin" style={{ fontSize: "2rem" }}>⏳</div>
    <div className="empty-state-title">Cargando...</div>
  </div>
);

// ==========================================
// HERO HEADER
// ==========================================

// Comparte el link de la app — usa el selector nativo del celular
// (WhatsApp, SMS, etc.) si el navegador lo soporta; si no (la mayoría de
// escritorio), abre WhatsApp Web directo con el mensaje ya armado. Nunca
// tira error visible: si el usuario cancela el selector nativo, el navigator
// share rechaza la promesa con AbortError, que acá simplemente se ignora.
const compartirApp = () => {
  const url = "https://reformas-aagg.com";
  const texto = "Mirá esta app para pedir presupuestos de reformas online, rápido y fácil:";
  if (navigator.share) {
    navigator.share({ title: "Reformas AAGG", text: texto, url }).catch(() => {});
  } else {
    window.open(`https://wa.me/?text=${encodeURIComponent(`${texto} ${url}`)}`, "_blank");
  }
};

const HeroHeader = ({ empresa }) => (
  <div className="hero-header" data-testid="hero-header">
    <button
      type="button"
      className="hero-header__share-btn"
      onClick={compartirApp}
      aria-label="Compartir la app"
      data-testid="compartir-app"
    >
      <Share size={18} weight="bold" />
    </button>
    <div style={{ position: "relative", zIndex: 1 }}>
      <div className="flex items-center gap-2 mb-4">
        <Badge variant="accent">
          <span className="animate-pulse" style={{ width: 6, height: 6, background: "#4ade80", borderRadius: "50%", display: "inline-block" }} />
          Disponible
        </Badge>
        <span className="text-tiny" style={{ opacity: 0.7 }}>{empresa?.direccion || "Servicio a domicilio"}</span>
      </div>

      {empresa?.logo_url ? (
        <div className="hero-header__logo-wrap">
          <img
            src={empresa.logo_url}
            alt={empresa?.nombre || "AAGG Reformas"}
            className="hero-header__logo"
          />
        </div>
      ) : (
        <h1 className="text-hero font-display" style={{ marginBottom: 8 }}>
          {empresa?.nombre?.split(" ")[0] || "Andy"}{" "}
          <span style={{ color: "var(--brand-accent)" }}>{empresa?.nombre?.split(" ")[1] || "Gonzaga"}</span>
        </h1>
      )}

      <p className="hero-header__headline">
        {empresa?.slogan || "Instalaciones y Reformas"}
      </p>

      <div className="flex gap-2 flex-wrap">
        <Badge variant="outline">⭐ +10 años experiencia</Badge>
        <Badge variant="outline">📋 Presupuesto 24h</Badge>
      </div>
    </div>
  </div>
);

// ==========================================
// NAVEGACIÓN INFERIOR
// ==========================================

const BottomNav = ({ activeTab, onNavigate, isAdmin }) => {
  const clientTabs = [
    { id: "portada", icon: <House size={22} weight={activeTab === "portada" ? "fill" : "regular"} />, label: "Inicio" },
    { id: "diagnostico", icon: <MagnifyingGlass size={22} weight={activeTab === "diagnostico" ? "fill" : "regular"} />, label: "Diagnóstico" },
    { id: "presupuesto", icon: <Calculator size={22} weight={activeTab === "presupuesto" ? "fill" : "regular"} />, label: "Presupuesto" },
    { id: "catalogo", icon: <Camera size={22} weight={activeTab === "catalogo" ? "fill" : "regular"} />, label: "Catálogo" },
  ];

  const adminTabs = [
    { id: "dashboard", icon: <ChartBar size={22} weight={activeTab === "dashboard" ? "fill" : "regular"} />, label: "Panel" },
    { id: "gestora", icon: <Sparkle size={22} weight={activeTab === "gestora" ? "fill" : "regular"} />, label: "Gestora" },
    { id: "admin", icon: <Gear size={22} weight={activeTab === "admin" ? "fill" : "regular"} />, label: "M\u00e1s" },
    { id: "presupuesto", icon: <Plus size={22} weight="bold" />, label: "Nuevo" },
    { id: "historial", icon: <FileText size={22} weight={activeTab === "historial" ? "fill" : "regular"} />, label: "Historial" },
  ];

  const tabs = isAdmin ? adminTabs : clientTabs;

  return (
    <nav className="bottom-nav" data-testid="bottom-nav">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onNavigate(tab.id)}
          className={`nav-item ${activeTab === tab.id ? "nav-item-active" : ""}`}
          data-testid={`nav-${tab.id}`}
        >
          <span className="nav-item-icon">{tab.icon}</span>
          <span className="nav-item-label">{tab.label}</span>
        </button>
      ))}
    </nav>
  );
};

// ==========================================
// PORTADA (LANDING)
// ==========================================

// Descripción corta por módulo para la portada — db.modulos no guarda esto,
// solo nombre/icono/foto, así que vive acá. Si aparece un módulo nuevo sin
// entrada, cae al genérico "Presupuesto a medida" (no rompe, no queda vacío).
const DESCRIPCIONES_MODULO = {
  suelos: "Laminado, tarima y vinílico",
  cocinas: "Reforma completa: muebles, encimeras y electrodomésticos",
  pintura_carpinteria: "Puertas, marcos y lacados",
  pintura_paramentos: "Paredes, techos y acabados",
  mamparas: "Instalación y sustitución",
  ceramica: "Baños y cocinas",
  instalaciones: "Puertas, ventanas y persianas",
  mantenimiento: "Fontanería y electricidad básica",
  cierres: "Cerraduras y seguridad",
  electricidad: "Puntos de luz e instalaciones",
  fontaneria: "Grifería y tuberías",
  tabiqueria: "Tabiques y trasdosados a medida",
  // Este es el módulo que más se confunde con Cocinas: cubre trabajos
  // sueltos (ej. cambiar solo los frentes/puertas de una cocina, sin tocar
  // el resto) — si no queda explícito acá, el cliente busca "frentes de
  // cocina" en el módulo Cocinas (reforma completa), no lo encuentra armado
  // así y se va sin pedir nada.
  trabajos_puntuales: "Frentes de cocina, electricidad, fontanería, estantes y arreglos sueltos",
  muebles: "Montaje de mobiliario",
  armarios: "Diseño e instalación a medida",
};

const Portada = ({ empresa, modulos, onNavigate, onAbrirAsesor }) => {
  // Antes esta lista estaba hardcodeada acá y con el tiempo se desalineó de
  // los módulos reales (le faltaban varios, tenía categorías que ya no
  // existen) — ahora sale directo de `modulos` (misma fuente que el resto de
  // la app), así nunca más se desincroniza.
  const servicios = (modulos || [])
    .slice()
    .sort((a, b) => (a.orden || 0) - (b.orden || 0))
    .map((m) => ({
      icon: m.icono,
      foto: m.foto_icono,
      nombre: m.nombre,
      desc: DESCRIPCIONES_MODULO[m.id] || "Presupuesto a medida",
    }));

  return (
    <div className="pb-24" data-testid="portada-screen">
      <HeroHeader empresa={empresa} />
      
      <div className="container" style={{ marginTop: -20, position: "relative", zIndex: 2 }}>
        {/* Beneficios */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {[
            { icon: "📋", title: "Presupuesto gratis", desc: "Sin compromiso" },
            { icon: "💬", title: "Asesor técnico", desc: "Te aconsejamos" },
            { icon: "🛡️", title: "Precio cerrado", desc: "Sin sorpresas" },
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="card card-elevated p-4"
            >
              <div style={{ fontSize: "1.5rem", marginBottom: 8 }}>{item.icon}</div>
              <div className="font-bold text-small">{item.title}</div>
              <div className="text-tiny text-secondary">{item.desc}</div>
            </motion.div>
          ))}
        </div>

        {/* CTAs principales */}
        <div className="space-y-3 mb-8">
          <Button 
            variant="accent" 
            className="btn-full btn-lg"
            onClick={() => onNavigate("presupuesto")}
            data-testid="btn-nuevo-presupuesto"
          >
            🆕 Solicitar presupuesto gratis
          </Button>
          <Button 
            variant="primary" 
            className="btn-full"
            onClick={() => onAbrirAsesor()}
            icon={<Brain size={20} weight="fill" />}
          >
            🧠 Consultar Asesor Técnico IA
          </Button>
          <Button 
            variant="outline" 
            className="btn-full"
            onClick={() => onNavigate("diagnostico")}
          >
            🔍 Diagnóstico rápido de tu obra
          </Button>
          <Button
            variant="ghost" 
            className="btn-full"
            onClick={() => onNavigate("tarjeta-digital")}
            icon={<QrCode size={20} />}
          >
            📱 Ver tarjeta digital
          </Button>
        </div>

        {/* Servicios */}
        <div className="section">
          <h2 className="section-title">
            <Wrench size={20} />
            Servicios profesionales
          </h2>
          <div className="card">
            {servicios.map((s, i) => (
              <div key={i} className="list-item">
                {s.foto ? (
                  <img src={s.foto} alt="" className="list-item-photo" />
                ) : (
                  <div className="list-item-icon">{s.icon}</div>
                )}
                <div className="list-item-content">
                  <div className="list-item-title">{s.nombre}</div>
                  <div className="list-item-subtitle">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contacto */}
        <div className="card card-dark p-5 text-center">
          <p className="text-tiny" style={{ opacity: 0.7, marginBottom: 8 }}>¿Prefieres llamar?</p>
          <a 
            href={`tel:${empresa?.telefono}`} 
            className="text-h2 font-display block mb-2"
            style={{ color: "var(--brand-accent)" }}
          >
            {empresa?.telefono || "6XX XXX XXX"}
          </a>
          <p className="text-tiny" style={{ opacity: 0.5 }}>
            {empresa?.email || "info@andygonzaga.com"}
          </p>
          <p className="text-tiny" style={{ opacity: 0.5, marginTop: 4 }}>
            📅 Disponibilidad: Sábados y Domingos
          </p>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// LISTA DE PRECIOS
// ==========================================

const ListaPrecios = ({ trabajos, modulos, isAdmin, onEdit }) => {
  const [moduloActivo, setModuloActivo] = useState(modulos[0]?.id || "suelos");
  const [busqueda, setBusqueda] = useState("");

  const trabajosFiltrados = trabajos.filter((t) => {
    const matchModulo = t.modulo === moduloActivo;
    const matchBusqueda = !busqueda || t.nombre.toLowerCase().includes(busqueda.toLowerCase());
    return matchModulo && matchBusqueda;
  });

  // Texto de la lista que se está viendo AHORA (respeta el filtro de módulo
  // y búsqueda en pantalla) — para reenviar puntualmente si alguien la pide,
  // sin tener que mandar el catálogo completo cada vez.
  const nombreModuloActivo = modulos.find((m) => m.id === moduloActivo)?.nombre || moduloActivo;
  const textoParaCompartir = () => {
    const lineas = trabajosFiltrados.map(
      (t) => `${t.nombre} — ECO ${t.precio_eco}€ / STD ${t.precio_std}€ / PREM ${t.precio_prem}€`
    );
    return `Precios ${nombreModuloActivo} — AAGG Reformas\n\n${lineas.join("\n")}`;
  };

  const compartirPorWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(textoParaCompartir())}`, "_blank");
  };

  const compartirPorCorreo = () => {
    const asunto = `Precios ${nombreModuloActivo} — AAGG Reformas`;
    window.location.href = `mailto:?subject=${encodeURIComponent(asunto)}&body=${encodeURIComponent(textoParaCompartir())}`;
  };

  return (
    <div className="pb-24" data-testid="precios-screen">
      <div className="container pt-4">
        <h1 className="text-h2 font-display mb-4">
          <Money size={28} style={{ display: "inline", marginRight: 8 }} />
          Consulta de Precios
        </h1>

        {isAdmin && (
          <div
            className="card mb-4 p-3"
            style={{ background: "rgba(26,58,74,0.06)", border: "1px solid rgba(26,58,74,0.15)" }}
          >
            <div className="text-tiny mb-2" style={{ color: "var(--info)" }}>
              Esta lista ya no es pública — si alguien la pide, reenviásela desde acá (manda solo
              lo que estás viendo ahora: el módulo y la búsqueda que tenés puestos).
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={compartirPorWhatsApp} className="flex-1">
                📱 WhatsApp
              </Button>
              <Button variant="outline" size="sm" onClick={compartirPorCorreo} className="flex-1">
                ✉️ Correo
              </Button>
            </div>
          </div>
        )}

        {!isAdmin && (
          <div
            className="card mb-4 p-3"
            style={{ background: "rgba(26,58,74,0.06)", border: "1px solid rgba(26,58,74,0.15)" }}
          >
            <div className="text-tiny" style={{ color: "var(--info)" }}>
              ℹ️ Estos precios son solo de <b>mano de obra</b>. Si el trabajo lleva material (grifos, azulejos, pintura, puertas, etc.), se suma aparte según lo que necesites — pedí un presupuesto para ver el total real con material incluido.
            </div>
          </div>
        )}

        {/* Búsqueda */}
        <div className="relative mb-4">
          <MagnifyingGlass 
            size={20} 
            className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: "var(--text-light)" }}
          />
          <input
            type="text"
            placeholder="Buscar trabajo..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="input"
            style={{ paddingLeft: 44 }}
          />
        </div>

        {/* Tabs módulos */}
        <div className="flex gap-2 overflow-x-auto pb-3 mb-4 scrollbar-hide">
          {modulos.map((mod) => (
            <Chip
              key={mod.id}
              active={moduloActivo === mod.id}
              onClick={() => setModuloActivo(mod.id)}
            >
              {mod.foto_icono ? (
                <img src={mod.foto_icono} alt="" className="chip-photo" />
              ) : (
                mod.icono
              )}{" "}
              {mod.nombre}
            </Chip>
          ))}
        </div>

        {/* Lista */}
        <div className="card">
          {trabajosFiltrados.length === 0 ? (
            <EmptyState
              icon="🔍"
              title="Sin resultados"
              description="No hay trabajos que coincidan"
            />
          ) : (
            trabajosFiltrados.map((trabajo) => (
              <div 
                key={trabajo.id} 
                className={`list-item ${isAdmin ? "list-item-interactive" : ""}`}
                onClick={() => isAdmin && onEdit && onEdit(trabajo)}
              >
                {trabajo.foto_url && (
                  <img
                    src={trabajo.foto_url}
                    alt={trabajo.nombre}
                    className="rounded-lg object-cover flex-shrink-0"
                    style={{ width: 40, height: 40, marginRight: 10 }}
                  />
                )}
                <div className="list-item-content">
                  <div className="list-item-title">{trabajo.nombre}</div>
                  <div className="list-item-subtitle">{trabajo.unidad}</div>
                </div>
                <div className="flex gap-2">
                  <span className="price-tag price-eco">{trabajo.precio_eco}€</span>
                  <span className="price-tag price-std">{trabajo.precio_std}€</span>
                  <span className="price-tag price-prem">{trabajo.precio_prem}€</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Trabajos de pintura carpintería donde el tamaño real (puerta/ventana "especial")
// cambia cuánta pintura hace falta. areaRef = m² de referencia de la medida estándar.
const PUERTAS_VENTANAS_TAMANO = {
  "Decapado puerta": 1.89,
  "Lacado puerta lisa 2 caras": 1.89,
  "Lacado puerta con molduras": 1.89,
  "Barnizado puerta madera": 1.89,
  "Esmalte ventana madera": 1.44,
  "Pintura puerta metálica/hierro": 1.89,
  "Pintura puerta aluminio": 1.89,
  "Pintura puerta de garaje": 5.5,
  "Puerta de armario/vestidor": 1.2,
  "Persiana/contraventana de madera": 1.44,
  // Mamparas: una "especial" (a medida) no es del mismo tamaño que una
  // estándar — antes cobraba lo mismo un panel de 70cm que uno de 180cm.
  "Instalación mampara frontal": 1.2 * 1.95,
  "Instalación mampara angular": 2 * 0.8 * 1.95,
  "Instalación mampara bañera": 0.8 * 1.4,
  "Instalación mampara semicircular": 3.12,
};

// Mamparas usan el mismo selector de Tamaño que puertas/ventanas, pero NO
// tiene sentido mostrarles "Exterior" (siempre son de baño, interior) ni
// "Tiene cristales" (ya son de cristal, no aplica el mismo significado que
// en una puerta/ventana de carpintería).
const MAMPARA_NOMBRES = ["Instalación mampara frontal", "Instalación mampara angular", "Instalación mampara bañera", "Instalación mampara semicircular"];

// Trabajos donde tiene sentido marcar "Exterior" aunque no tengan tamaño
// variable (reja y pasamanos no se miden por ancho x alto, pero sí pueden
// ser interior o exterior y eso cambia la pintura que hace falta).
const PINTURA_CARP_EXTERIOR_EXTRA = ["Esmalte reja/barandilla", "Pasamanos de escalera (madera)"];

// Suelo de cerámica exterior (terraza/patio) necesita gres antideslizante,
// no el mismo producto que un suelo de interior.
const CERAMICA_EXTERIOR_APLICA = ["Solado gres estándar", "Solado porcelánico grande"];

// Alicatado sobre pladur/tabique seco necesita adhesivo flexible +
// imprimación, no el cemento cola normal (el cemento no agarra bien sobre
// yeso). Solo aplica a paredes, no a suelo.
const CERAMICA_PLADUR_APLICA = ["Alicatado pared estándar", "Alicatado formato grande"];

// Silicona sanitaria no es automática: solo hace falta si en la zona a
// alicatar/solar hay plato de ducha, bañera o sanitario que sellar contra
// el azulejo (una cocina o un pasillo no la necesitan).
const CERAMICA_SANITARIOS_APLICA = ["Alicatado pared estándar", "Alicatado formato grande", "Solado gres estándar", "Solado porcelánico grande"];

// Pintura/barnizado de muebles se cobra por m², pero en vez de pedirle al
// cliente que calcule el área a mano, se le puede pedir Alto x Ancho x
// Profundo y la app calcula la superficie a pintar sola (frente + 2 costados
// + tapa, sin contar la trasera que va contra la pared).
const MUEBLES_CALCULABLES = ["Pintura de mueble (armario, cómoda)", "Barnizado de mueble de madera"];

// Trabajos de pintura interior donde puede aplicar "Zona húmeda" (baño/cocina),
// que cambia la pintura a una satinada lavable con antimoho en vez de la mate
// estándar del resto de la casa.
const PINTURA_ZONA_HUMEDA_APLICA = [
  "Pintura plástica lisa 1 capa",
  "Pintura plástica lisa 2 capas",
  "Pintura gotelé 1 capa",
  "Pintura gotelé 2 capas",
  "Pintura techo liso 2 capas",
];

// ==========================================
// CREAR PRESUPUESTO (4 PASOS)
// ==========================================

const CrearPresupuesto = ({ trabajos, modulos, factores, empresa, onCrear, onBack, presupuestoInicial, modoOperario = false, modoPrueba = false, adminToken = null }) => {
  // Si venimos de "← Volver" desde el resumen de un presupuesto recién
  // guardado (para agregar un trabajo que faltó), arrancamos con todos sus
  // datos ya cargados en el paso 2 (Trabajos), en vez de un presupuesto
  // en blanco desde el paso 1.
  // Si viene de "← Volver" (edición de un presupuesto ya guardado, con id)
  // arranca en el paso de Trabajos. Si viene del Diagnóstico Inteligente
  // (líneas ya armadas pero sin id, todavía no se guardó nada) arranca en
  // el paso de Cliente para pedir nombre/teléfono antes de mostrar la
  // revisión, ya con las líneas precargadas.
  const [paso, setPaso] = useState(presupuestoInicial?.id ? 2 : 1);
  const [cliente, setCliente] = useState(presupuestoInicial ? {
    nombre: presupuestoInicial.cliente_nombre || "",
    telefono: presupuestoInicial.cliente_telefono || "",
    email: presupuestoInicial.cliente_email || "",
    direccion: presupuestoInicial.cliente_direccion || "",
  } : modoPrueba ? { nombre: "PRUEBAS DESDE APP", telefono: "600000000", email: "", direccion: "" }
  : { nombre: "", telefono: "", email: "", direccion: "" });
  const [calidad, setCalidad] = useState(presupuestoInicial?.calidad || "std");
  const [tipoPresupuesto, setTipoPresupuesto] = useState(presupuestoInicial?.tipo_presupuesto || "mo");
  const [lineas, setLineas] = useState(presupuestoInicial?.lineas || []);
  const [factoresSeleccionados, setFactoresSeleccionados] = useState(
    presupuestoInicial?.factores_aplicados
      ? presupuestoInicial.factores_aplicados
          .map((fa) => factores.find((f) => f.id === fa.factor_id))
          .filter(Boolean)
      : []
  );
  const [descuento, setDescuento] = useState(presupuestoInicial?.descuento_porcentaje || 0);
  const [incluyeIva, setIncluyeIva] = useState(presupuestoInicial?.incluye_iva ?? true);
  // Tipo de cliente decide el % de IVA por defecto: 10% particular (vivienda
  // habitual >2 años), 21% empresa/local comercial — antes quedaba fijo en
  // 21% siempre, sin importar a quién se le facturaba.
  const [tipoCliente, setTipoCliente] = useState(presupuestoInicial?.tipo_cliente || "particular");
  // Editable a mano (no solo derivado de tipoCliente): un Particular con
  // reforma en vivienda habitual solo tiene IVA 10% si el material que pone
  // el profesional no supera el 40% de la base imponible (Hacienda) — si lo
  // supera, TODA la factura pasa a 21%, aunque el cliente sea particular.
  // Antes esto quedaba fijo en 10% para Particular sin excepción, generando
  // facturas con el IVA mal puesto en trabajos con mucho material.
  const [ivaPorcentaje, setIvaPorcentaje] = useState(
    presupuestoInicial?.iva_porcentaje ?? (tipoCliente === "empresa" ? 21 : 10)
  );
  const [descripcion, setDescripcion] = useState(presupuestoInicial?.descripcion_cliente || "");
  // Calendario de disponibilidad: fechaInicioDeseada es lo único editable
  // acá (lo que pide el cliente, o lo que Andy define directo en modo
  // operario). Los otros tres viajan sin tocar en cada guardado para no
  // pisar en silencio una fecha ya confirmada/repropuesta desde Presupuestos
  // Pendientes cuando se edita el presupuesto por otro motivo (ej. agregar
  // un trabajo que faltó) — ver _calcular_presupuesto_completo en el backend,
  // que reemplaza estos campos con lo que reciba en cada guardado.
  const [fechaInicioDeseada, setFechaInicioDeseada] = useState(presupuestoInicial?.fecha_inicio_estimada || "");
  const [estadoFecha, setEstadoFecha] = useState(presupuestoInicial?.estado_fecha || "");
  const [fechaInicioPropuestaAdmin, setFechaInicioPropuestaAdmin] = useState(presupuestoInicial?.fecha_inicio_propuesta_admin || "");
  const [notaFechaAdmin, setNotaFechaAdmin] = useState(presupuestoInicial?.nota_fecha_admin || "");
  const [editandoId, setEditandoId] = useState(presupuestoInicial?.id || null);
  const [moduloActivo, setModuloActivo] = useState(modulos[0]?.id || "suelos");
  const moduloActivoObj = modulos.find((m) => m.id === moduloActivo);
  const [verGuiaModulos, setVerGuiaModulos] = useState(false);
  // Alto real del panel del prototipo (nos lo manda el propio iframe cada vez
  // que su contenido cambia de tamaño) — sin esto el iframe queda fijo a 80vh
  // sin importar cuánto contenido tenga, dejando huecos enormes en pantallas
  // cortas (ej. "Trabajo añadido") que esconden los botones Volver/Continuar
  // muy abajo, sobre todo en mobile.
  // Guardado por módulo (no un solo valor) a propósito: el listener de abajo
  // vive dentro de un useEffect con deps [calidad], así que su closure queda
  // "congelado" con el moduloActivo de cuando se montó — comparar ahí mismo
  // contra moduloActivo (closure viejo) hacía que la altura nunca actualizara
  // al cambiar de módulo. Guardando por clave y leyendo moduloActivo recién
  // en el render (siempre actual) se evita el problema del todo.
  const [alturaPorModulo, setAlturaPorModulo] = useState({});
  const prototipoIframeRef = useRef(null);
  // Snapshot del % de IVA SOLO al entrar a cada módulo de prototipo (no
  // reactivo a ivaPorcentaje en general) — si el iframe recibiera el
  // ivaPorcentaje actual en su src cada vez que cambia, React le pondría un
  // src nuevo y el navegador recargaría el iframe entero cada vez que el
  // propio usuario elige 10%/21% ahí adentro (perdiendo su lugar). El valor
  // real elegido viaja por postMessage (ver aplicarEstadoInicial de cada
  // prototipo y el listener de abajo), esto es solo el punto de partida.
  const ivaInicialParaIframe = useMemo(() => ivaPorcentaje, [moduloActivo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escucha lo que le pasa el prototipo embebido (Tabiquería, y los que se
  // vayan agregando) y lo integra al presupuesto general: sus líneas se suman
  // a `lineas` (reemplazando las que ya tenía ese módulo, para no duplicar al
  // recibir un aviso nuevo), su tipo de presupuesto ajusta el precio con la
  // misma regla que el resto de la app (solo MO = 60% del precio con
  // material), y su foto/descripción quedan guardadas para el resto del flujo.
  useEffect(() => {
    function onMensajePrototipo(e) {
      if (!e.data || typeof e.data !== "object") return;
      if (e.data.type === "prototipo:cart") {
        const mod = e.data.modulo;
        const tipoP = e.data.tipoPresupuesto || "mo";
        const nuevasDelModulo = (e.data.cart || []).map((it, idx) => {
          // Los electrodomésticos "los compra Andy" (Cocinas) no son mano de
          // obra — es plata real que Andy adelanta por el aparato en sí. Si
          // se les aplicara el mismo 60% de "solo mano de obra" que al resto
          // de las líneas, Andy termina cobrando de menos por algo que ya
          // pagó completo (encontrado probando un presupuesto real de punta
          // a punta, 8 sep 2026). Van siempre al precio completo, sin
          // importar si el resto del presupuesto es "solo mano de obra".
          const factor = (tipoP === "mo" && !it.esElectro) ? 0.6 : 1;
          return {
          // trabajo_id es obligatorio (string) en el backend — si el prototipo
          // no manda uno real (no debería pasar, pero por las dudas) se genera
          // uno sintético para no romper el cálculo de totales en silencio.
          trabajo_id: it.trabajoId || `proto-${mod}-${idx}`,
          trabajo_nombre: it.label + (it.note ? ` (${it.note})` : ""),
          cantidad: it.qty,
          unidad: it.unidad,
          precio_unitario: Math.round((it.precioTotal / it.qty) * factor * 100) / 100,
          subtotal: it.precioTotal * factor,
          calidad,
          incluye_material: tipoP === "mo_material" || !!it.esElectro,
          precio_material: 0,
          tiempo_estimado: it.horasTotal,
          fuente_prototipo: mod,
          // Flags opcionales que algún módulo puede necesitar para que el
          // motor de materiales viejo calcule bien (ej. Alicatado: silicona
          // sanitaria solo si la línea tiene plato/bañera que sellar) — se
          // reenvían tal cual si el prototipo los manda, sin forzar nada.
          ...(it.con_sanitarios ? { con_sanitarios: true } : {}),
          // Mampara: tamaño real de la pieza en metros, para que el motor de
          // materiales escale el kit de vidrio contra su tamaño de referencia
          // en vez de comprar siempre el mismo kit sin importar el tamaño real.
          ...(it.ancho_m ? { ancho_m: it.ancho_m } : {}),
          ...(it.alto_m ? { alto_m: it.alto_m } : {}),
          // Pintura Carpintería: exterior (formulación distinta), con_cristales
          // (menos superficie a pintar) y nivel_oxido (metal, antes de imprimar).
          ...(it.exterior ? { exterior: true } : {}),
          ...(it.con_cristales ? { con_cristales: true } : {}),
          ...(it.nivel_oxido && it.nivel_oxido !== "ninguno" ? { nivel_oxido: it.nivel_oxido } : {}),
          // Pintura Paramentos: baño/cocina necesita pintura satinada
          // antimoho, no la mate estándar.
          ...(it.zona_humeda ? { zona_humeda: true } : {}),
          // Cocinas: cajones reales del módulo (para que el backend compre
          // la cantidad correcta de pares de guías/rieles, no una estimación).
          ...(it.cantidadCajones ? { cantidad_cajones: it.cantidadCajones } : {}),
          };
        });
        setLineas((prev) => prev.filter((l) => l.fuente_prototipo !== mod).concat(nuevasDelModulo));
        if (e.data.fotoEstado) setFotoEstado(e.data.fotoEstado);
        if (e.data.firma) setFirma(e.data.firma);
        // La descripción ya no viaja por acá — vive en el paso 1, a nivel de
        // todo el presupuesto, no por módulo (ver comentario en el paso 1).
        // El propio prototipo trae su selector de tipo de presupuesto (para no
        // duplicarlo en esta pantalla) — lo que elige ahí pasa a ser también el
        // tipo del presupuesto general, para que el guardado final (PDF,
        // WhatsApp, "Presupuesto creado") lo muestre igual a como se armó.
        if (e.data.tipoPresupuesto) setTipoPresupuesto(e.data.tipoPresupuesto);
        // El propio prototipo vuelve a ser la única fuente real de IVA (con/
        // sin Y ahora también el %, agregado hoy dentro de cada prototipo) —
        // la tarjeta de IVA del padre se oculta de nuevo para estos módulos
        // (ver más abajo), así no queda duplicada con la del prototipo como
        // le pasó a Andy probando en vivo (todos los módulos con iframe
        // mostraban dos controles de IVA distintos, sin sincronizar entre
        // sí). Esto NO dispara una recarga del iframe: el src usa
        // ivaInicialParaIframe (congelado al entrar al módulo), no
        // ivaPorcentaje en vivo.
        if (e.data.iva) setIncluyeIva(e.data.iva === "con");
        if (e.data.ivaPorcentaje) setIvaPorcentaje(e.data.ivaPorcentaje);
      }
      if (e.data.type === "prototipo:altura" && e.data.modulo) {
        setAlturaPorModulo((prev) => ({ ...prev, [e.data.modulo]: e.data.altura }));
      }
      if (e.data.type === "prototipo:volver") {
        // Vuelve a la pantalla de pestañas de módulos (no directo al resumen
        // final) para que el cliente pueda sumar trabajos de otro módulo
        // (Suelos, Cocinas...) al mismo presupuesto antes de cerrarlo.
        setPaso(2);
      }
      if (e.data.type === "prototipo:ver_resumen") {
        // El prototipo tenía su propio resumen interno (con firma/WhatsApp
        // propios) pensado para cuando se usaba suelto, sin la app alrededor.
        // Embebido, ese resumen queda duplicado con el real — "Ver mi lista
        // completa" salta directo acá en vez de mostrar esa pantalla vieja.
        setPaso(3);
      }
      if (e.data.type === "prototipo:salir") {
        // El prototipo mismo nos avisa que ya estaba en su menú principal
        // cuando le llegó el "Volver" — no lo decidimos nosotros con
        // prototipoEnHub (que puede estar un instante desactualizado si el
        // usuario clickea "Volver" dos veces seguido rápido).
        setPaso(1);
      }
      // El prototipo avisa "ready" recién cuando ya cargó su catálogo real —
      // ahí es seguro mandarle de vuelta las líneas que ya tenía este
      // presupuesto (si se abrió para editar uno existente), para que no
      // arranque vacío y el usuario pierda lo que había cargado antes.
      if (e.data.type === "prototipo:ready") {
        const mod = e.data.modulo;
        const lineasDelModulo = (presupuestoInicial?.lineas || []).filter((l) => l.fuente_prototipo === mod);
        if (lineasDelModulo.length && e.source) {
          const factorGeneral = (presupuestoInicial?.tipo_presupuesto || "mo") === "mo" ? 0.6 : 1;
          const cart = lineasDelModulo.map((l) => {
            // Mismo criterio que al guardar: las líneas de electrodomésticos
            // (Cocinas) nunca se escalaron al 60%, así que al reconstruir el
            // carrito para volver a editar tampoco hay que desescalarlas —
            // si no, el precio que el prototipo muestra al reabrir queda
            // 1/0.6 más alto del que realmente se cobró.
            const esElectro = /^(Electrodoméstico|Grifería):/.test(l.trabajo_nombre || "");
            const factor = esElectro ? 1 : factorGeneral;
            return {
              trabajoId: l.trabajo_id,
              label: l.trabajo_nombre,
              qty: l.cantidad,
              unidad: l.unidad,
              horasTotal: l.tiempo_estimado,
              precioTotal: l.subtotal / factor,
            };
          });
          e.source.postMessage({
            type: "prototipo:init",
            modulo: mod,
            cart,
            tipoPresupuesto: presupuestoInicial?.tipo_presupuesto || "mo",
            iva: (presupuestoInicial?.incluye_iva ?? true) ? "con" : "sin",
            ivaPorcentaje: presupuestoInicial?.iva_porcentaje || ivaPorcentaje,
          }, "*");
        }
      }
    }
    window.addEventListener("message", onMensajePrototipo);
    return () => window.removeEventListener("message", onMensajePrototipo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calidad]);

  const [modalTrabajo, setModalTrabajo] = useState(null);
  const [cantidad, setCantidad] = useState(1);
  const [cantidadExtra, setCantidadExtra] = useState(0);
  const [puertaEspecial, setPuertaEspecial] = useState(false);
  const [anchoM, setAnchoM] = useState("");
  const [altoM, setAltoM] = useState("");
  const [exterior, setExterior] = useState(false);
  const [conCristales, setConCristales] = useState(false);
  const [zonaHumeda, setZonaHumeda] = useState(false);
  const [pladur, setPladur] = useState(false);
  const [conSanitarios, setConSanitarios] = useState(false);
  const [usarMedidasMueble, setUsarMedidasMueble] = useState(false);
  const [muebleAlto, setMuebleAlto] = useState("");
  const [muebleAncho, setMuebleAncho] = useState("");
  const [muebleProfundo, setMuebleProfundo] = useState("");
  const [totales, setTotales] = useState({});
  const [firma, setFirma] = useState(null);
  const [fotoEstado, setFotoEstado] = useState(null);
  const [humedadSoporte, setHumedadSoporte] = useState(
    presupuestoInicial?.respuestas_diagnostico?.estado_soporte === "Con humedad visible"
  );

  // Calcular totales
  useEffect(() => {
    const calcular = async () => {
      if (lineas.length === 0) {
        setTotales({});
        return;
      }
      try {
        const res = await api.post("/calcular-presupuesto", {
          lineas,
          factores_aplicados: factoresSeleccionados.map((f) => ({
            factor_id: f.id,
            nombre: f.nombre,
            multiplicador: f.multiplicador,
            tipo: f.tipo || "multiplicador",
            valor: f.valor_fijo || 0,
          })),
          descuento_porcentaje: descuento,
          incluye_iva: incluyeIva,
          iva_porcentaje: ivaPorcentaje,
        });

        // Calcular materiales de forma REALISTA
        let total_final = res.total;
        
        if (tipoPresupuesto === "mo_material") {
          // Estimar materiales de forma más precisa (30-35% del trabajo total, no 40%)
          // Para trabajos de alicatado/cerámica: más realista
          const estimado_materiales = lineas.reduce((sum, l) => {
            // Si es trabajo de cerámica/alicatado, usar cálculo más preciso
            const trabajo_nombre = l.trabajo_nombre.toLowerCase();
            if (trabajo_nombre.includes('cerám') || trabajo_nombre.includes('alicatado') || trabajo_nombre.includes('azulejo')) {
              // Para cerámica: materiales son ~30% del precio total (no 40%)
              return sum + (l.subtotal * 0.3);
            }
            // Para otros trabajos, usar 35%
            return sum + (l.subtotal * 0.35);
          }, 0);
          total_final = res.total + estimado_materiales;
        }
        
        setTotales({...res, total_final});
      } catch (e) {
        console.error(e);
      }
    };
    calcular();
  }, [lineas, factoresSeleccionados, descuento, incluyeIva, tipoPresupuesto, ivaPorcentaje]);

  const factorTamanoModal = () => {
    const areaRef = modalTrabajo && PUERTAS_VENTANAS_TAMANO[modalTrabajo.nombre];
    const anchoNum = Number(anchoM) || 0;
    const altoNum = Number(altoM) || 0;
    if (areaRef && puertaEspecial && anchoNum > 0 && altoNum > 0) {
      return (anchoNum * altoNum) / areaRef;
    }
    return 1;
  };

  // Superficie a pintar de un mueble: frente + 2 costados + tapa (sin la
  // trasera, que va contra la pared)
  const areaMuebleCalculada = () => {
    const alto = Number(muebleAlto) || 0;
    const ancho = Number(muebleAncho) || 0;
    const profundo = Number(muebleProfundo) || 0;
    if (alto <= 0 || ancho <= 0 || profundo <= 0) return 0;
    return ancho * alto + 2 * profundo * alto + ancho * profundo;
  };

  useEffect(() => {
    if (modalTrabajo && MUEBLES_CALCULABLES.includes(modalTrabajo.nombre) && usarMedidasMueble) {
      const area = areaMuebleCalculada();
      if (area > 0) setCantidad(Math.round(area * 100) / 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usarMedidasMueble, muebleAlto, muebleAncho, muebleProfundo]);

  const agregarLinea = () => {
    if (!modalTrabajo || cantidad <= 0) return;
    const precioKey = `precio_${calidad}`;
    let precioUnitario = modalTrabajo[precioKey];
    
    // CRÍTICO: Ajustar precio según modalidad (solo MO vs MO+Material)
    if (tipoPresupuesto === "mo") {
      // Solo mano de obra = 60% del precio total (backend calculará materiales aparte)
      precioUnitario = precioUnitario * 0.6;
    }
    // Si es "mo_material" usa el precio completo

    // Puerta/ventana "especial" (a medida): el precio de mano de obra también
    // escala con el tamaño real, no solo el material
    const areaRef = PUERTAS_VENTANAS_TAMANO[modalTrabajo.nombre];
    const anchoNum = Number(anchoM) || 0;
    const altoNum = Number(altoM) || 0;
    const factorTamano = factorTamanoModal();
    precioUnitario = precioUnitario * factorTamano;

    const nuevaLinea = {
      trabajo_id: modalTrabajo.id,
      trabajo_nombre: modalTrabajo.nombre,
      cantidad,
      unidad: modalTrabajo.unidad,
      precio_unitario: precioUnitario,
      subtotal: precioUnitario * cantidad,
      calidad,
      incluye_material: tipoPresupuesto === "mo_material",
      precio_material: 0, // Backend calculará materiales
      tiempo_estimado: Math.round((modalTrabajo.tiempo_base_horas || 0.5) * cantidad * factorTamano * 100) / 100,
    };
    if (modalTrabajo.nombre === "Montaje mueble bajo (cajones)") {
      nuevaLinea.cantidad_cajones = cantidadExtra;
    }
    if (modalTrabajo.nombre === "Montaje mueble columna") {
      nuevaLinea.cantidad_puertas = cantidadExtra;
    }
    if (areaRef) {
      if (puertaEspecial && anchoNum > 0 && altoNum > 0) {
        nuevaLinea.ancho_m = anchoNum;
        nuevaLinea.alto_m = altoNum;
      }
      nuevaLinea.con_cristales = conCristales;
    }
    if (areaRef || PINTURA_CARP_EXTERIOR_EXTRA.includes(modalTrabajo.nombre) || CERAMICA_EXTERIOR_APLICA.includes(modalTrabajo.nombre)) {
      nuevaLinea.exterior = exterior;
    }
    if (PINTURA_ZONA_HUMEDA_APLICA.includes(modalTrabajo.nombre)) {
      nuevaLinea.zona_humeda = zonaHumeda;
    }
    if (CERAMICA_PLADUR_APLICA.includes(modalTrabajo.nombre)) {
      nuevaLinea.pladur = pladur;
    }
    if (CERAMICA_SANITARIOS_APLICA.includes(modalTrabajo.nombre)) {
      nuevaLinea.con_sanitarios = conSanitarios;
    }
    setLineas([...lineas, nuevaLinea]);
    setModalTrabajo(null);
    setCantidad(1);
    setCantidadExtra(0);
    setPuertaEspecial(false);
    setAnchoM("");
    setAltoM("");
    setExterior(false);
    setConCristales(false);
    setZonaHumeda(false);
    setPladur(false);
    setConSanitarios(false);
    setUsarMedidasMueble(false);
    setMuebleAlto("");
    setMuebleAncho("");
    setMuebleProfundo("");
  };

  const eliminarLinea = (index) => {
    setLineas(lineas.filter((_, i) => i !== index));
  };

  const toggleFactor = (factor) => {
    const exists = factoresSeleccionados.find((f) => f.id === factor.id);
    if (exists) {
      setFactoresSeleccionados(factoresSeleccionados.filter((f) => f.id !== factor.id));
    } else {
      setFactoresSeleccionados([...factoresSeleccionados, factor]);
    }
  };

  const guardarPresupuesto = async () => {
    try {
      // La única firma que puede existir hoy es la que manda un prototipo
      // embebido (ej. Mampara) por postMessage — ver e.data.firma más abajo.
      // El paso 3 externo de firma se sacó del flujo (no tenía efecto real).
      const firmaData = firma || null;

      // Inferir tipo_trabajo desde el módulo o primer trabajo
      let tipo_trabajo_inferido = moduloActivo || "";
      if (lineas.length > 0 && lineas[0].trabajo_nombre) {
        tipo_trabajo_inferido = lineas[0].trabajo_nombre.toLowerCase();
      }

      const data = {
        cliente_nombre: cliente.nombre,
        cliente_telefono: cliente.telefono,
        cliente_email: cliente.email,
        cliente_direccion: cliente.direccion,
        calidad,
        tipo_presupuesto: tipoPresupuesto,
        tipo_trabajo: tipo_trabajo_inferido,
        respuestas_diagnostico: humedadSoporte ? { estado_soporte: "Con humedad visible" } : {},
        lineas,
        factores_aplicados: factoresSeleccionados.map((f) => ({
          factor_id: f.id,
          nombre: f.nombre,
          multiplicador: f.multiplicador,
          tipo: f.tipo || "multiplicador",
          valor: f.valor_fijo || 0,
        })),
        descuento_porcentaje: descuento,
        incluye_iva: incluyeIva,
        iva_porcentaje: ivaPorcentaje,
        tipo_cliente: tipoCliente,
        descripcion_cliente: descripcion,
        firma_cliente: firmaData,
        foto_estado_inicial: fotoEstado,
        // Si ya tenía un estado más avanzado (en_revision, aceptado...) lo preservamos:
        // guardar una edición no debe hacer retroceder el seguimiento del admin.
        estado: presupuestoInicial?.estado || "pendiente",
        fecha_inicio_estimada: fechaInicioDeseada,
        estado_fecha: estadoFecha,
        fecha_inicio_propuesta_admin: fechaInicioPropuestaAdmin,
        nota_fecha_admin: notaFechaAdmin,
      };
      
      const res = editandoId
        ? await api.put(`/presupuestos/${editandoId}`, { ...data, token_acceso: presupuestoInicial?.token_acceso }, adminToken)
        : await api.post("/presupuestos", data);
      // La pantalla "¡Presupuesto creado!" es otro componente, sin acceso al
      // módulo activo — le pasamos el color acá para que se retema igual.
      onCrear(moduloActivoObj && moduloActivoObj.color ? { ...res, _temaColor: moduloActivoObj.color, _temaColorPrimary: moduloActivoObj.colorPrimary } : res);
    } catch (e) {
      console.error(e);
      alert("Error al guardar");
    }
  };

  // Nivelado autonivelante vive en el catálogo de Suelos, pero un suelo de
  // cerámica con desnivel también lo necesita antes del solado. En vez de
  // duplicar el trabajo en el catálogo (dos trabajos idénticos con distinto
  // módulo), se muestra el mismo trabajo también en la pestaña Cerámica.
  const trabajosFiltrados = trabajos.filter((t) =>
    t.modulo === moduloActivo ||
    (moduloActivo === "ceramica" && t.modulo === "suelos" && t.nombre.toLowerCase().includes("nivelado")) ||
    (moduloActivo === "mamparas" && t.nombre.toLowerCase() === "cambio grifo ducha/bañera")
  );

  const factoresPorCategoria = {
    acceso: factores.filter((f) => f.categoria === "acceso"),
    ubicacion: factores.filter((f) => f.categoria === "ubicacion"),
    urgencia: factores.filter((f) => f.categoria === "urgencia"),
    estado: factores.filter((f) => f.categoria?.startsWith("estado")),
  };

  // Del paso 2 en adelante, si el módulo activo tiene un prototipo propio (con
  // su propia paleta), el resto del recorrido (resumen final, "presupuesto
  // creado") se retema con ese color en vez del dorado genérico de la app —
  // así no se siente como que el cliente "sale" de la herramienta que estaba
  // usando justo antes de confirmar. En el paso 1 (todavía no se eligió
  // módulo) se deja el color de siempre.
  const temaPrototipo = paso >= 2 && moduloActivoObj && moduloActivoObj.color
    ? { "--brand-primary": moduloActivoObj.colorPrimary || moduloActivoObj.color, "--brand-accent": moduloActivoObj.color, "--brand-accent-light": moduloActivoObj.color }
    : undefined;

  return (
    <div className="pb-24" data-testid="presupuesto-screen" style={temaPrototipo}>
      <div className="container pt-4">
        {/* Progress */}
        <div className="progress-bar mb-6">
          {[1, 2, 3].map((p) => (
            <div 
              key={p} 
              className={`progress-step ${paso >= p ? "progress-step-active" : ""}`}
            />
          ))}
        </div>

        {/* PASO 1: Cliente y Calidad */}
        {paso === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-h2 font-display">
                <Badge variant="primary">1</Badge> Cliente y calidad
              </h2>
              <button
                onClick={onBack}
                className="btn btn-ghost btn-sm"
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <X size={20} /> Cancelar
              </button>
            </div>

            {/* Modo operario (armado en la casa del cliente): los datos
                personales todavía no bloquean el avance — se piden recién en
                el Paso 3, antes de guardar, una vez que el presupuesto ya
                está armado. La calidad y las condiciones de acceso de abajo
                sí se dejan acá, porque afectan el precio en vivo mientras se
                arma delante del cliente. */}
            {modoOperario && !modoPrueba && (
              <div className="card p-3 mb-4" style={{ background: "color-mix(in srgb, var(--info) 10%, white)", border: "1px solid var(--info)" }}>
                <div className="text-tiny" style={{ color: "var(--info)" }}>
                  🧑‍🔧 Estás armando este presupuesto vos, delante del cliente. Podés saltear el nombre y el teléfono — te los voy a pedir al final, antes de guardar.
                </div>
              </div>
            )}

            {modoPrueba && (
              <div className="card p-3 mb-4" style={{ background: "color-mix(in srgb, var(--warning, orange) 12%, white)", border: "1px solid var(--warning, orange)" }}>
                <div className="text-tiny" style={{ color: "var(--warning, orange)" }}>
                  🧪 Modo prueba — esto NO es un presupuesto real. Se guarda como "PRUEBAS DESDE APP" para que lo identifiques y borres cuando quieras en Pendientes/Historial. Usalo para verificar que cada módulo dé bien precios, materiales y horas.
                </div>
              </div>
            )}

            <div className="card p-4 mb-4">
              <Input
                label={modoOperario ? "Nombre / Empresa (opcional acá)" : "Nombre / Empresa"}
                placeholder="Nombre del cliente"
                value={cliente.nombre}
                onChange={(e) => setCliente({ ...cliente, nombre: e.target.value })}
              />
              <label className="input-label mt-4">Tipo de cliente</label>
              <div className="quality-selector">
                {[
                  { id: "particular", label: "🏠 Particular", desc: "Vivienda habitual, IVA 10%" },
                  { id: "empresa", label: "🏢 Empresa", desc: "Local/oficina, IVA 21%" },
                ].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => { setTipoCliente(t.id); setIvaPorcentaje(t.id === "empresa" ? 21 : 10); }}
                    className={`quality-option ${tipoCliente === t.id ? "quality-option-active" : ""}`}
                  >
                    <div className="font-bold text-small">{t.label}</div>
                    <div className="text-tiny text-secondary">{t.desc}</div>
                  </button>
                ))}
              </div>
              <Input
                label="Teléfono"
                type="tel"
                placeholder="6XX XXX XXX"
                value={cliente.telefono}
                onChange={(e) => setCliente({ ...cliente, telefono: e.target.value })}
              />
              <Input
                label="Email (opcional)"
                type="email"
                placeholder="email@ejemplo.com"
                value={cliente.email}
                onChange={(e) => setCliente({ ...cliente, email: e.target.value })}
              />
              <Input
                label="Dirección de la obra"
                placeholder="Calle, número, población"
                value={cliente.direccion}
                onChange={(e) => setCliente({ ...cliente, direccion: e.target.value })}
              />
              <Input
                label="¿Cuándo te gustaría empezar? (opcional)"
                type="date"
                min={new Date().toISOString().slice(0, 10)}
                value={fechaInicioDeseada}
                onChange={(e) => {
                  const v = e.target.value;
                  setFechaInicioDeseada(v);
                  // Si el estado de la fecha ya estaba avanzado (confirmada/
                  // repropuesta, viene de un presupuesto que se está
                  // editando) y acá se toca la fecha de nuevo, vuelve a
                  // "pendiente" — cambió el pedido, Andy tiene que revisarlo
                  // de nuevo. Si se borra el campo, queda sin pedido.
                  setEstadoFecha(v ? "pendiente" : "");
                }}
              />
              {fechaInicioDeseada && (
                <p className="text-tiny text-secondary" style={{ marginTop: "-8px", marginBottom: "12px" }}>
                  📅 Es orientativo — te confirmamos la fecha (o te proponemos otra) cuando revisemos el presupuesto.
                </p>
              )}

              <label className="input-label mt-4">Calidad del trabajo</label>
              <div className="quality-selector">
                {[
                  { id: "eco", label: "💚 ECO", desc: "Económico" },
                  { id: "std", label: "🔵 STD", desc: "Recomendado" },
                  { id: "prem", label: "🟠 PREMIUM", desc: "Alta gama" },
                ].map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setCalidad(c.id)}
                    className={`quality-option quality-option-${c.id} ${calidad === c.id ? "quality-option-active" : ""}`}
                  >
                    <div className="font-bold text-small">{c.label}</div>
                    <div className="text-tiny text-secondary">{c.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Factores de acceso */}
            <div className="card card-dark p-4 mb-4">
              <h3 className="font-bold mb-3" style={{ color: "var(--brand-accent)" }}>
                📍 Condiciones de acceso
              </h3>
              <div className="space-y-2">
                {factoresPorCategoria.acceso.slice(0, 6).map((factor) => {
                  const isSelected = factoresSeleccionados.find((f) => f.id === factor.id);
                  const esFijoHora = factor.tipo === "fijo_hora";
                  const isAumento = esFijoHora ? (factor.valor_fijo || 0) > 0 : factor.multiplicador > 1;
                  return (
                    <div
                      key={factor.id}
                      onClick={() => toggleFactor(factor)}
                      className={`factor-card ${isSelected ? "factor-card-selected" : ""}`}
                    >
                      <div className={`factor-checkbox ${isSelected ? "factor-checkbox-checked" : ""}`}>
                        {isSelected && <Check size={14} weight="bold" style={{ color: "var(--brand-primary)" }} />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className="font-bold text-small">{factor.icono} {factor.nombre}</div>
                        <div className="text-tiny" style={{ opacity: 0.7 }}>{factor.descripcion}</div>
                      </div>
                      <span className={`factor-percentage ${isAumento ? "factor-increase" : "factor-decrease"}`}>
                        {esFijoHora
                          ? (isAumento ? `+${factor.valor_fijo}h` : "0h")
                          : `${isAumento ? "+" : ""}${((factor.multiplicador - 1) * 100).toFixed(0)}%`}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Estado del soporte (solo relevante si hay suelos en el presupuesto) */}
            <div className="card card-dark p-4 mb-4">
              <div
                onClick={() => setHumedadSoporte(!humedadSoporte)}
                className={`factor-card ${humedadSoporte ? "factor-card-selected" : ""}`}
              >
                <div className={`factor-checkbox ${humedadSoporte ? "factor-checkbox-checked" : ""}`}>
                  {humedadSoporte && <Check size={14} weight="bold" style={{ color: "var(--brand-primary)" }} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div className="font-bold text-small">💧 Soporte con humedad visible</div>
                  <div className="text-tiny" style={{ opacity: 0.7 }}>Si vas a instalar suelo nuevo sobre una base húmeda, se añade barrera de vapor</div>
                </div>
              </div>
            </div>

            {/* Descripción + foto juntas acá, antes de elegir módulo/trabajos
                — así el cliente cuenta qué necesita y muestra el espacio
                desde el principio (como arrancaría un mensaje real de
                WhatsApp), no recién al final. Antes vivían separadas (la
                descripción metida dentro de cada módulo con prototipo, la
                foto en el paso 3) y encima se perdía una si el cliente
                combinaba más de un módulo en el mismo presupuesto — quedaba
                solo la del último módulo tocado. Una sola acá, a nivel de
                todo el presupuesto, resuelve las dos cosas. */}
            <div className="card p-4 mb-4">
              <Textarea
                label="Descripción del trabajo (opcional)"
                placeholder="Contanos qué necesitás..."
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
              />
              <label className="input-label mt-4">📷 Foto del espacio (opcional)</label>
              <p className="text-tiny text-secondary mb-3">
                Ayuda a entender mejor el trabajo antes de presupuestarlo
              </p>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => setFotoEstado(reader.result);
                    reader.readAsDataURL(file);
                  }
                }}
                style={{ display: 'none' }}
                id="foto-input-paso1"
              />
              <label htmlFor="foto-input-paso1" style={{ width: '100%', cursor: 'pointer' }}>
                <div className="btn btn-outline" style={{ width: '100%', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <Camera size={20} /> Tomar/Subir foto
                </div>
              </label>
              {fotoEstado && (
                <img src={fotoEstado} alt="Estado" className="mt-3 rounded-lg w-full h-40 object-cover" />
              )}
            </div>

            {/* El botón de abajo queda bloqueado sin nombre/teléfono, pero
                no explicaba por qué — el cliente tocaba y no pasaba nada.
                Encontrado por Andy probando la app. */}
            {!modoOperario && (!cliente.nombre || !cliente.telefono) && (
              <div className="text-tiny mb-3" style={{ color: "var(--warning)" }}>
                ⚠️ Completá tu nombre y número de móvil para continuar.
              </div>
            )}

            <Button
              variant="accent"
              className="btn-full"
              onClick={() => setPaso(2)}
              disabled={!modoOperario && (!cliente.nombre || !cliente.telefono)}
              data-testid="btn-continuar-paso2"
            >
              Continuar → Elegir trabajos
            </Button>
          </motion.div>
        )}

        {/* PASO 2: Trabajos */}
        {paso === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            {/* Antes había acá un botón "Atrás" duplicado del "Volver" de más
                abajo (mismo setPaso, misma función) — un cliente real no
                tenía forma de saber que hacían lo mismo. Se dejó solo el de
                abajo, junto al botón que avanza. */}
            <div className="mb-4">
              <h2 className="text-h2 font-display">
                <Badge variant="primary">2</Badge> Trabajos del presupuesto
              </h2>
            </div>

            {/* Guía "qué cubre cada categoría" — un cliente que busca un
                trabajo suelto (ej. "cambiar solo los frentes de la
                cocina") tiende a entrar al módulo con el nombre más obvio
                (Cocinas = reforma completa), no lo encuentra armado así y
                se va sin pedir nada. Esto le muestra, antes de tocar
                ninguna pestaña, en qué categoría vive cada tipo de
                trabajo — sin adivinar nada por palabras clave de la
                descripción del Paso 1 (eso sería más frágil: "cambiar el
                frente de mi cocina" matchea "cocina" y lo mandaría justo
                al módulo equivocado). */}
            <div
              className="card p-3 mb-3"
              style={{ cursor: "pointer" }}
              onClick={() => setVerGuiaModulos(!verGuiaModulos)}
            >
              <div className="flex items-center justify-between">
                <div className="text-small font-bold" style={{ color: "var(--info)" }}>
                  ℹ️ ¿No sabés en qué categoría está tu trabajo?
                </div>
                <CaretDown
                  size={16}
                  style={{
                    color: "var(--info)",
                    transform: verGuiaModulos ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 150ms ease",
                    flexShrink: 0,
                  }}
                />
              </div>
              {verGuiaModulos && (
                <div className="mt-3" style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {modulos
                    .slice()
                    .sort((a, b) => (a.orden || 0) - (b.orden || 0))
                    .map((mod) => (
                      <div key={mod.id} className="text-tiny">
                        <span className="font-bold">{mod.nombre}:</span>{" "}
                        <span className="text-secondary">{DESCRIPCIONES_MODULO[mod.id] || "Presupuesto a medida"}</span>
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* Tabs módulos */}
            <div className="flex gap-2 overflow-x-auto pb-2 mb-1 scrollbar-hide">
              {modulos.map((mod) => (
                <Chip
                  key={mod.id}
                  active={moduloActivo === mod.id}
                  onClick={() => setModuloActivo(mod.id)}
                >
                  {mod.foto_icono ? (
                    <img src={mod.foto_icono} alt="" className="chip-photo" />
                  ) : (
                    mod.icono
                  )}{" "}
                  {mod.nombre}
                </Chip>
              ))}
            </div>
            {moduloActivoObj && (
              <p className="text-tiny text-secondary mb-3">
                {DESCRIPCIONES_MODULO[moduloActivo] || "Presupuesto a medida"}
              </p>
            )}

            {moduloActivoObj && moduloActivoObj.prototipo_url ? (
              <div className="card mb-4 p-0" style={{ overflow: "hidden" }}>
                <iframe
                  ref={prototipoIframeRef}
                  title={moduloActivoObj.nombre}
                  src={`${moduloActivoObj.prototipo_url}?api=${encodeURIComponent(API)}&calidad=${calidad}&iva=${ivaInicialParaIframe}`}
                  style={{ width: "100%", height: alturaPorModulo[moduloActivo] ? `${alturaPorModulo[moduloActivo] + 8}px` : "80vh", border: 0, display: "block" }}
                />
              </div>
            ) : (
            <>
            {moduloActivo === "suelos" && (
              <div
                className="card mb-3 p-3"
                style={{ background: "rgba(26,58,74,0.06)", border: "1px solid rgba(26,58,74,0.15)" }}
              >
                <div className="text-tiny" style={{ color: "var(--info)" }}>
                  ℹ️ El aislante bajo el suelo se calcula automáticamente según los m² y se agrega en el presupuesto final, junto con el pavimento y el rodapié.
                </div>
              </div>
            )}

            {moduloActivo === "ceramica" && (
              <div
                className="card mb-3 p-3"
                style={{ background: "rgba(26,58,74,0.06)", border: "1px solid rgba(26,58,74,0.15)" }}
              >
                <div className="text-tiny" style={{ color: "var(--info)" }}>
                  ℹ️ Si el suelo tiene desnivel, agregá "Nivelado autonivelante" (está en esta misma lista) antes del solado. Si es pared de pladur, marcá el check "Pladur" al agregar el trabajo de alicatado.
                </div>
              </div>
            )}

            {moduloActivo === "mamparas" && (
              <div
                className="card mb-3 p-3"
                style={{ background: "rgba(26,58,74,0.06)", border: "1px solid rgba(26,58,74,0.15)" }}
              >
                <div className="text-tiny" style={{ color: "var(--info)" }}>
                  ℹ️ Casi siempre que se instala una mampara también se cambia el grifo de la ducha — "Cambio grifo ducha/bañera" está en esta misma lista, no hace falta ir a otra pestaña.
                </div>
              </div>
            )}

            {/* Lista de trabajos */}
            <div className="card mb-4" style={{ maxHeight: 250, overflowY: "auto" }}>
              {trabajosFiltrados.map((trabajo) => (
                <div
                  key={trabajo.id}
                  onClick={() => {
                    setModalTrabajo(trabajo);
                    setCantidad(1);
                    setCantidadExtra(0);
                    setPuertaEspecial(false);
                    setAnchoM("");
                    setAltoM("");
                    setExterior(false);
                    setConCristales(false);
                    setZonaHumeda(false);
                    setPladur(false);
                    setConSanitarios(false);
                    setUsarMedidasMueble(false);
                    setMuebleAlto("");
                    setMuebleAncho("");
                    setMuebleProfundo("");
                  }}
                  className="list-item list-item-interactive"
                >
                  {trabajo.foto_url && (
                    <img
                      src={trabajo.foto_url}
                      alt={trabajo.nombre}
                      className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                      style={{ marginRight: 10 }}
                    />
                  )}
                  <div className="list-item-content">
                    <div className="list-item-title">{trabajo.nombre}</div>
                    <div className="list-item-subtitle">
                      {trabajo.unidad} · {calidad.toUpperCase()}: {trabajo[`precio_${calidad}`]}€
                    </div>
                  </div>
                  <button className="btn-icon btn-accent">
                    <Plus size={20} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
            </>
            )}

            {/* Líneas añadidas — las del módulo con prototipo activo ya se ven
                adentro de su propio panel (iframe), no hace falta repetirlas
                acá abajo; sí se siguen mostrando las de otros módulos. */}
            {lineas.length > 0 && (() => {
              // idx acá siempre es el índice real dentro de "lineas" (no del
              // subconjunto filtrado) — eliminarLinea() lo necesita así.
              const itemsVisibles = lineas
                .map((linea, idx) => ({ linea, idx }))
                .filter(({ linea }) => !(moduloActivoObj && moduloActivoObj.prototipo_url) || linea.fuente_prototipo !== moduloActivo);
              const esFiltradoPorOtroModulo = !!(moduloActivoObj && moduloActivoObj.prototipo_url);
              return itemsVisibles.length > 0 && (
              <div className="mb-4">
                <label className="input-label">
                  {esFiltradoPorOtroModulo ? `Ya agregado en otro módulo (${itemsVisibles.length})` : `Líneas añadidas (${itemsVisibles.length})`}
                </label>
                <div className="space-y-2">
                  {itemsVisibles.map(({ linea, idx }) => (
                    <div key={idx} className="card p-3 flex items-center gap-3">
                      <div style={{ flex: 1 }}>
                        <div className="font-bold text-small">{linea.trabajo_nombre}</div>
                        <div className="text-tiny text-secondary">
                          {modoOperario
                            ? <>{linea.cantidad} {linea.unidad} × {formatCurrency(linea.precio_unitario)}</>
                            : <>{linea.cantidad} {linea.unidad}</>}
                        </div>
                      </div>
                      {/* Precio/subtotal por línea solo visible en modo operario (Andy) — al
                          cliente self-service no se le muestra el desglose de precios por
                          trabajo, solo el total final, para no exponer la lista de precios
                          completa a quien solo quiere mirar sin ser un cliente real. */}
                      {modoOperario && <div className="font-bold">{formatCurrency(linea.subtotal)}</div>}
                      <button
                        onClick={() => eliminarLinea(idx)}
                        className="btn-icon-sm"
                        style={{ background: "rgba(193,41,46,0.1)", color: "var(--error)" }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Totales */}
                <div className="totals-card mt-3">
                  <div className="totals-row">
                    <span className="text-secondary">Mano de Obra</span>
                    <span className="font-bold">{formatCurrency(totales.total || 0)}</span>
                  </div>
                  {tipoPresupuesto === "mo_material" && (
                    <div className="totals-row">
                      <span className="text-secondary">Materiales (estimado)</span>
                      <span className="font-bold">{formatCurrency((totales.total_final || 0) - (totales.total || 0))}</span>
                    </div>
                  )}
                  <div className="totals-row totals-row-main">
                    <span>TOTAL {tipoPresupuesto === "mo" ? "(Solo MO)" : "(MO + Material)"}</span>
                    <span>{formatCurrency(totales.total_final || totales.total || 0)}</span>
                  </div>
                </div>
              </div>
              );
            })()}

            {/* Tipo presupuesto / IVA / Descripción — si el módulo activo es
                un prototipo embebido, ya tiene su propia versión de estos
                campos (con/sin IVA Y el % 10/21, agregado hoy) y los manda
                por postMessage — acá no se duplican. Antes esta tarjeta de
                IVA se mostraba siempre (para poder elegir 21% incluso dentro
                de Trabajos Puntuales), pero eso duplicaba el control de IVA
                en TODOS los módulos de prototipo, no solo ese — encontrado
                por Andy probando varios módulos en vivo. El % de IVA
                correcto ahora se elige adentro del prototipo mismo. */}
            {!(moduloActivoObj && moduloActivoObj.prototipo_url) && (
              <div className="card p-4 mb-4">
                <label className="input-label">Tipo de presupuesto</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setTipoPresupuesto("mo")}
                    className={`quality-option ${tipoPresupuesto === "mo" ? "quality-option-active" : ""}`}
                  >
                    <div className="font-bold text-small">🔧 Solo mano obra</div>
                  </button>
                  <button
                    onClick={() => setTipoPresupuesto("mo_material")}
                    className={`quality-option ${tipoPresupuesto === "mo_material" ? "quality-option-active" : ""}`}
                  >
                    <div className="font-bold text-small">📦 MO + Material</div>
                  </button>
                </div>

                <label className="input-label mt-4">IVA</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setIncluyeIva(true)}
                    className={`quality-option ${incluyeIva ? "quality-option-active" : ""}`}
                  >
                    <div className="font-bold text-small">Con IVA ({ivaPorcentaje}%)</div>
                  </button>
                  <button
                    onClick={() => setIncluyeIva(false)}
                    className={`quality-option ${!incluyeIva ? "quality-option-active" : ""}`}
                  >
                    <div className="font-bold text-small">Sin IVA</div>
                  </button>
                </div>
                {incluyeIva && (
                  <>
                    <label className="input-label mt-3">Porcentaje de IVA</label>
                    <p className="text-tiny text-secondary mb-2">
                      Particular es 10% solo si el material que ponés no supera el 40% del total — si lo supera, corresponde 21% aunque sea vivienda habitual.
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setIvaPorcentaje(10)}
                        className={`quality-option ${ivaPorcentaje === 10 ? "quality-option-active" : ""}`}
                      >
                        <div className="font-bold text-small">10%</div>
                      </button>
                      <button
                        onClick={() => setIvaPorcentaje(21)}
                        className={`quality-option ${ivaPorcentaje === 21 ? "quality-option-active" : ""}`}
                      >
                        <div className="font-bold text-small">21%</div>
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="flex gap-3 mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  // Si hay un prototipo embebido, le delegamos la decisión:
                  // el prototipo mismo sabe (en el momento del click, no con
                  // un aviso previo que podría estar desactualizado) si está
                  // en su hub o no, y responde con "prototipo:salir" (sale
                  // de verdad al paso anterior) o vuelve a su hub interno.
                  if (moduloActivoObj?.prototipo_url) {
                    prototipoIframeRef.current?.contentWindow?.postMessage({ type: "app:volver" }, "*");
                    return;
                  }
                  setPaso(1);
                }}
                className="flex-1"
              >
                <CaretLeft size={18} /> Volver
              </Button>
              <Button
                variant="accent"
                onClick={() => setPaso(3)}
                disabled={lineas.length === 0}
                className="flex-1"
              >
                Ver resumen <CaretRight size={18} />
              </Button>
            </div>
          </motion.div>
        )}

        {/* PASO 3: Resumen */}
        {paso === 3 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="mb-4">
              <h2 className="text-h2 font-display">
                <Badge variant="primary">3</Badge> Resumen final
              </h2>
            </div>

            {/* Modo operario: acá es donde se piden nombre/teléfono/dirección
                que se saltearon en el Paso 1 — el presupuesto ya está armado
                (líneas, precios, condiciones), así que preguntarlos ahora no
                le hace perder tiempo al cliente mientras el profesional
                todavía está definiendo el trabajo. */}
            {modoOperario && (
              <div className="card p-4 mb-4">
                <label className="input-label mb-2" style={{ display: "block" }}>Datos del cliente</label>
                <Input
                  label="Nombre / Empresa"
                  placeholder="Nombre del cliente"
                  value={cliente.nombre}
                  onChange={(e) => setCliente({ ...cliente, nombre: e.target.value })}
                />
                <Input
                  label="Teléfono"
                  type="tel"
                  placeholder="6XX XXX XXX"
                  value={cliente.telefono}
                  onChange={(e) => setCliente({ ...cliente, telefono: e.target.value })}
                />
                <Input
                  label="Dirección de la obra"
                  placeholder="Calle, número, población"
                  value={cliente.direccion}
                  onChange={(e) => setCliente({ ...cliente, direccion: e.target.value })}
                />
                <Input
                  label="¿Cuándo quiere empezar? (opcional)"
                  type="date"
                  min={new Date().toISOString().slice(0, 10)}
                  value={fechaInicioDeseada}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFechaInicioDeseada(v);
                    setEstadoFecha(v ? "pendiente" : "");
                  }}
                />
                {(!cliente.nombre || !cliente.telefono) && (
                  <div className="text-tiny" style={{ color: "var(--warning)" }}>
                    Nombre y teléfono son necesarios para guardar y poder mandarle el presupuesto por WhatsApp.
                  </div>
                )}
              </div>
            )}

            {/* Cabecera cliente */}
            <div className="card card-dark p-4 mb-4">
              <div className="text-tiny" style={{ opacity: 0.6 }}>Presupuesto para</div>
              <div className="text-h3 font-display" style={{ color: "var(--brand-accent)" }}>
                {cliente.nombre || "Sin nombre todavía"}
              </div>
              <div className="text-small" style={{ opacity: 0.7, marginTop: 4 }}>
                <MapPin size={14} style={{ display: "inline", marginRight: 4 }} />
                {cliente.direccion}
              </div>
              {cliente.telefono && (
                <div className="text-small" style={{ opacity: 0.7 }}>
                  <Phone size={14} style={{ display: "inline", marginRight: 4 }} />
                  {cliente.telefono}
                </div>
              )}
              {fechaInicioDeseada && (
                <div className="text-small" style={{ opacity: 0.7 }}>
                  <Calendar size={14} style={{ display: "inline", marginRight: 4 }} />
                  Inicio deseado: {fechaInicioDeseada}
                </div>
              )}
            </div>

            {/* Detalle líneas */}
            <div className="card p-4 mb-4">
              <label className="input-label">Trabajos incluidos</label>
              {lineas.map((linea, idx) => (
                <div key={idx} className="flex justify-between py-2 border-b border-subtle">
                  <div>
                    <div className="font-bold text-small">{linea.trabajo_nombre}</div>
                    <div className="text-tiny text-secondary">
                      {modoOperario
                        ? <>{linea.cantidad} {linea.unidad} × {linea.precio_unitario}€</>
                        : <>{linea.cantidad} {linea.unidad}</>}
                    </div>
                  </div>
                  {modoOperario && <div className="font-bold">{formatCurrency(linea.subtotal)}</div>}
                </div>
              ))}

              {/* Factores — se muestra el importe en € que suma/resta cada uno,
                  no el multiplicador abstracto (×1.35), para que quede claro
                  a dónde va ese dinero y el total no se sienta como un cobro
                  no explicado. */}
              {factoresSeleccionados.length > 0 && (
                <div className="mt-4 pt-4 border-t border-subtle">
                  <label className="input-label">Recargos/descuentos por condiciones</label>
                  {(totales.factores_desglose || []).map((f, idx) => (
                    <div key={idx} className="flex justify-between text-tiny py-1">
                      <span>
                        {f.nombre}
                        {f.porcentaje !== null && ` (${f.porcentaje > 0 ? "+" : ""}${f.porcentaje}%)`}
                      </span>
                      <span style={{ color: f.importe > 0 ? "var(--error)" : "var(--success)" }}>
                        {f.importe > 0 ? "+" : ""}{formatCurrency(f.importe)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Totales finales */}
            <div className="card p-4 mb-4">
              <div className="totals-row">
                <span className="text-secondary">Base</span>
                <span className="font-bold">{formatCurrency(totales.base_imponible)}</span>
              </div>
              {totales.factor_total !== 1 && (
                <div className="totals-row">
                  <span className="text-secondary">Con factores</span>
                  <span className="font-bold">{formatCurrency(totales.base_con_factores)}</span>
                </div>
              )}
              {descuento > 0 && (
                <div className="totals-row" style={{ color: "var(--success)" }}>
                  <span>Descuento ({descuento}%)</span>
                  <span className="font-bold">-{formatCurrency(totales.descuento_importe)}</span>
                </div>
              )}
              {incluyeIva && (
                <div className="totals-row">
                  <span className="text-secondary">IVA {totales.iva_porcentaje ?? ivaPorcentaje}%</span>
                  <span className="font-bold">{formatCurrency(totales.iva_importe)}</span>
                </div>
              )}
              <div className="totals-row totals-row-main">
                <span>TOTAL</span>
                <span>{formatCurrency(totales.total)}</span>
              </div>
            </div>

            {/* Forma de pago */}
            <div className="card p-4 mb-4" style={{ background: "color-mix(in srgb, var(--brand-accent) 10%, white)", border: "1px solid var(--brand-accent)" }}>
              <label className="input-label" style={{ color: "var(--brand-accent)" }}>💳 Forma de pago</label>
              <div className="flex justify-between text-small mb-1">
                <span>Anticipo al inicio (40%)</span>
                <span className="font-bold" style={{ color: "var(--brand-accent)" }}>{formatCurrency(totales.anticipo_importe)}</span>
              </div>
              <div className="flex justify-between text-small mb-1">
                <span>Mitad de obra (30%)</span>
                <span className="font-bold">{formatCurrency(totales.mitad_obra_importe)}</span>
              </div>
              <div className="flex justify-between text-small">
                <span>Resto al finalizar (30%)</span>
                <span className="font-bold">{formatCurrency(totales.resto_importe)}</span>
              </div>
            </div>

            {/* Tiempo estimado */}
            {totales.tiempo_total_horas > 0 && (
              <div className="card p-4 mb-4">
                <label className="input-label">⏱️ Tiempo estimado</label>
                <div className="text-h3 font-display">
                  {totales.tiempo_total_horas < 8 
                    ? `${totales.tiempo_total_horas.toFixed(1)} horas`
                    : `${Math.ceil(totales.tiempo_total_horas / 8)} días laborables`
                  }
                </div>
              </div>
            )}

            {/* Botones finales */}
            <div className="space-y-3">
              <Button
                variant="accent"
                className="btn-full btn-lg"
                onClick={guardarPresupuesto}
                disabled={modoOperario && (!cliente.nombre || !cliente.telefono)}
                data-testid="btn-guardar-presupuesto"
              >
                <Check size={20} /> {editandoId ? "Guardar cambios" : "Guardar presupuesto"}
              </Button>
              <Button variant="outline" onClick={() => setPaso(2)} className="btn-full">
                <CaretLeft size={18} /> Volver a editar
              </Button>
            </div>
          </motion.div>
        )}

        {/* Modal cantidad */}
        <Modal
          isOpen={!!modalTrabajo}
          onClose={() => setModalTrabajo(null)}
          title="Añadir trabajo"
        >
          {modalTrabajo && (
            <div>
              {modalTrabajo.foto_url && (
                <img
                  src={modalTrabajo.foto_url}
                  alt={modalTrabajo.nombre}
                  className="w-full rounded-lg object-cover mb-3"
                  style={{ maxHeight: 180 }}
                />
              )}
              <div className="text-h3 font-display mb-1">{modalTrabajo.nombre}</div>
              <div className="text-secondary mb-4">{modalTrabajo.unidad}</div>

              <div className="flex items-center justify-center gap-4 mb-6">
                <button
                  onClick={() => setCantidad(Math.max(0.5, cantidad - 0.5))}
                  className="btn-icon btn-outline"
                  style={{ width: 48, height: 48 }}
                >
                  <Minus size={20} weight="bold" />
                </button>
                <input
                  type="number"
                  value={cantidad}
                  onChange={(e) => setCantidad(Number(e.target.value))}
                  className="input"
                  style={{ 
                    width: 100, 
                    textAlign: "center", 
                    fontSize: "1.5rem", 
                    fontWeight: 800,
                    fontFamily: "Chivo, sans-serif"
                  }}
                />
                <button
                  onClick={() => setCantidad(cantidad + 0.5)}
                  className="btn-icon btn-outline"
                  style={{ width: 48, height: 48 }}
                >
                  <Plus size={20} weight="bold" />
                </button>
              </div>

              {(modalTrabajo.nombre === "Montaje mueble bajo (cajones)" || modalTrabajo.nombre === "Montaje mueble columna") && (
                <div className="card mb-4 p-3" style={{ background: "rgba(26,58,74,0.06)", border: "1px solid rgba(26,58,74,0.15)" }}>
                  <label className="text-tiny font-bold mb-2" style={{ display: "block" }}>
                    {modalTrabajo.nombre === "Montaje mueble bajo (cajones)"
                      ? "Número total de cajones"
                      : "Número total de puertas"}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={cantidadExtra}
                    onChange={(e) => setCantidadExtra(Math.max(0, Number(e.target.value)))}
                    className="input"
                    style={{ width: "100%", textAlign: "center", fontSize: "1.2rem", fontWeight: 700 }}
                  />
                  <div className="text-tiny text-secondary mt-2">
                    {modalTrabajo.nombre === "Montaje mueble bajo (cajones)"
                      ? "Sumá todos los cajones de todos los muebles que elegiste acá arriba. Ej: un mueble con 3 cajones + otro con 1 = poné 4."
                      : "Sumá todas las puertas de todas las columnas que elegiste acá arriba."}
                  </div>
                </div>
              )}

              {MUEBLES_CALCULABLES.includes(modalTrabajo.nombre) && (
                <div className="card mb-4 p-3" style={{ background: "rgba(26,58,74,0.06)", border: "1px solid rgba(26,58,74,0.15)" }}>
                  <div
                    onClick={() => setUsarMedidasMueble(!usarMedidasMueble)}
                    className={`factor-card factor-card-light ${usarMedidasMueble ? "factor-card-selected" : ""}`}
                  >
                    <div className={`factor-checkbox factor-checkbox-light ${usarMedidasMueble ? "factor-checkbox-checked" : ""}`}>
                      {usarMedidasMueble && <Check size={14} weight="bold" style={{ color: "var(--brand-primary)" }} />}
                    </div>
                    <div className="text-small font-bold">Calcular m² por medidas</div>
                  </div>
                  {usarMedidasMueble && (
                    <>
                      <div className="flex gap-2 mt-3 mb-1">
                        <div style={{ flex: 1 }}>
                          <label className="text-tiny text-secondary">Alto (m)</label>
                          <input
                            type="number" min="0" step="0.05"
                            value={muebleAlto}
                            onChange={(e) => setMuebleAlto(e.target.value)}
                            className="input" placeholder="ej. 2.00"
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label className="text-tiny text-secondary">Ancho (m)</label>
                          <input
                            type="number" min="0" step="0.05"
                            value={muebleAncho}
                            onChange={(e) => setMuebleAncho(e.target.value)}
                            className="input" placeholder="ej. 1.20"
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label className="text-tiny text-secondary">Profundo (m)</label>
                          <input
                            type="number" min="0" step="0.05"
                            value={muebleProfundo}
                            onChange={(e) => setMuebleProfundo(e.target.value)}
                            className="input" placeholder="ej. 0.60"
                          />
                        </div>
                      </div>
                      <div className="text-tiny text-secondary">
                        {areaMuebleCalculada() > 0
                          ? `Superficie a pintar (frente + 2 costados + tapa): ${Math.round(areaMuebleCalculada() * 100) / 100} m²`
                          : "Completá las 3 medidas para calcular el área"}
                      </div>
                    </>
                  )}
                </div>
              )}

              {(PUERTAS_VENTANAS_TAMANO[modalTrabajo.nombre] || PINTURA_CARP_EXTERIOR_EXTRA.includes(modalTrabajo.nombre) || CERAMICA_EXTERIOR_APLICA.includes(modalTrabajo.nombre)) && (
                <div className="card mb-4 p-3" style={{ background: "rgba(26,58,74,0.06)", border: "1px solid rgba(26,58,74,0.15)" }}>
                  {PUERTAS_VENTANAS_TAMANO[modalTrabajo.nombre] && (
                    <>
                      <label className="text-tiny font-bold mb-2" style={{ display: "block" }}>Tamaño</label>
                      <div className="flex gap-2 mb-3">
                        <button
                          onClick={() => setPuertaEspecial(false)}
                          className={`btn btn-sm ${!puertaEspecial ? "btn-accent" : "btn-outline"}`}
                          style={{ flex: 1 }}
                        >
                          Estándar
                        </button>
                        <button
                          onClick={() => setPuertaEspecial(true)}
                          className={`btn btn-sm ${puertaEspecial ? "btn-accent" : "btn-outline"}`}
                          style={{ flex: 1 }}
                        >
                          Especial (a medida)
                        </button>
                      </div>
                      {puertaEspecial && (
                        <div className="flex gap-2 mb-1">
                          <div style={{ flex: 1 }}>
                            <label className="text-tiny text-secondary">Ancho (m)</label>
                            <input
                              type="number" min="0" step="0.05"
                              value={anchoM}
                              onChange={(e) => setAnchoM(e.target.value)}
                              className="input" placeholder="ej. 1.20"
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label className="text-tiny text-secondary">Alto (m)</label>
                            <input
                              type="number" min="0" step="0.05"
                              value={altoM}
                              onChange={(e) => setAltoM(e.target.value)}
                              className="input" placeholder="ej. 2.40"
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {!MAMPARA_NOMBRES.includes(modalTrabajo.nombre) && (
                    <div
                      onClick={() => setExterior(!exterior)}
                      className={`factor-card factor-card-light ${exterior ? "factor-card-selected" : ""}`}
                      style={{ marginTop: 10 }}
                    >
                      <div className={`factor-checkbox factor-checkbox-light ${exterior ? "factor-checkbox-checked" : ""}`}>
                        {exterior && <Check size={14} weight="bold" style={{ color: "var(--brand-primary)" }} />}
                      </div>
                      <div className="text-small font-bold">Exterior</div>
                    </div>
                  )}
                  {PUERTAS_VENTANAS_TAMANO[modalTrabajo.nombre] && !MAMPARA_NOMBRES.includes(modalTrabajo.nombre) && (
                    <div
                      onClick={() => setConCristales(!conCristales)}
                      className={`factor-card factor-card-light ${conCristales ? "factor-card-selected" : ""}`}
                      style={{ marginTop: 6 }}
                    >
                      <div className={`factor-checkbox factor-checkbox-light ${conCristales ? "factor-checkbox-checked" : ""}`}>
                        {conCristales && <Check size={14} weight="bold" style={{ color: "var(--brand-primary)" }} />}
                      </div>
                      <div className="text-small font-bold">Tiene cristales</div>
                    </div>
                  )}
                </div>
              )}

              {PINTURA_ZONA_HUMEDA_APLICA.includes(modalTrabajo.nombre) && (
                <div className="card mb-4 p-3" style={{ background: "rgba(26,58,74,0.06)", border: "1px solid rgba(26,58,74,0.15)" }}>
                  <div
                    onClick={() => setZonaHumeda(!zonaHumeda)}
                    className={`factor-card factor-card-light ${zonaHumeda ? "factor-card-selected" : ""}`}
                  >
                    <div className={`factor-checkbox factor-checkbox-light ${zonaHumeda ? "factor-checkbox-checked" : ""}`}>
                      {zonaHumeda && <Check size={14} weight="bold" style={{ color: "var(--brand-primary)" }} />}
                    </div>
                    <div className="text-small font-bold">Zona húmeda (baño/cocina)</div>
                  </div>
                  <div className="text-tiny text-secondary" style={{ marginTop: 6 }}>
                    Usa pintura satinada lavable con antimoho en vez de la mate estándar del resto de la casa.
                  </div>
                </div>
              )}

              {CERAMICA_PLADUR_APLICA.includes(modalTrabajo.nombre) && (
                <div className="card mb-4 p-3" style={{ background: "rgba(26,58,74,0.06)", border: "1px solid rgba(26,58,74,0.15)" }}>
                  <div
                    onClick={() => setPladur(!pladur)}
                    className={`factor-card factor-card-light ${pladur ? "factor-card-selected" : ""}`}
                  >
                    <div className={`factor-checkbox factor-checkbox-light ${pladur ? "factor-checkbox-checked" : ""}`}>
                      {pladur && <Check size={14} weight="bold" style={{ color: "var(--brand-primary)" }} />}
                    </div>
                    <div className="text-small font-bold">Pared de pladur / tabique seco</div>
                  </div>
                  <div className="text-tiny text-secondary" style={{ marginTop: 6 }}>
                    Necesita imprimación + adhesivo flexible especial, el cemento cola normal no agarra bien sobre yeso.
                  </div>
                </div>
              )}

              {CERAMICA_SANITARIOS_APLICA.includes(modalTrabajo.nombre) && (
                <div className="card mb-4 p-3" style={{ background: "rgba(26,58,74,0.06)", border: "1px solid rgba(26,58,74,0.15)" }}>
                  <div
                    onClick={() => setConSanitarios(!conSanitarios)}
                    className={`factor-card factor-card-light ${conSanitarios ? "factor-card-selected" : ""}`}
                  >
                    <div className={`factor-checkbox factor-checkbox-light ${conSanitarios ? "factor-checkbox-checked" : ""}`}>
                      {conSanitarios && <Check size={14} weight="bold" style={{ color: "var(--brand-primary)" }} />}
                    </div>
                    <div className="text-small font-bold">Hay plato de ducha/bañera/sanitario a sellar</div>
                  </div>
                  <div className="text-tiny text-secondary" style={{ marginTop: 6 }}>
                    Agrega silicona sanitaria para el perímetro. No marcar si es cocina, pasillo u otra zona sin sanitarios.
                  </div>
                </div>
              )}

              <div className="text-center mb-4">
                <div className="text-tiny text-secondary">Precio unitario ({calidad.toUpperCase()})</div>
                <div className="text-h3 font-display">
                  {formatCurrency(modalTrabajo[`precio_${calidad}`] * factorTamanoModal())}/{modalTrabajo.unidad}
                </div>
                {factorTamanoModal() !== 1 && (
                  <div className="text-tiny text-secondary">Ajustado por tamaño real (x{factorTamanoModal().toFixed(2)})</div>
                )}
              </div>

              <div className="text-center mb-6">
                <div className="text-tiny text-secondary">Total línea</div>
                <div className="text-h2 font-display" style={{ color: "var(--brand-accent)" }}>
                  {formatCurrency(modalTrabajo[`precio_${calidad}`] * factorTamanoModal() * cantidad)}
                </div>
              </div>

              <Button variant="accent" className="btn-full" onClick={agregarLinea}>
                <Check size={20} /> Añadir al presupuesto
              </Button>
            </div>
          )}
        </Modal>
      </div>
      <AsistenteFlotantePresupuesto
        trabajos={trabajos}
        calidad={calidad}
        tipoPresupuesto={tipoPresupuesto}
        lineas={lineas}
        cliente={cliente}
        onAgregarLinea={(nuevaLinea) => setLineas((prev) => [...prev, nuevaLinea])}
      />
    </div>
  );
};

// ==========================================
// RESUMEN PRESUPUESTO CREADO
// ==========================================

const ResumenPresupuestoCreado = ({ presupuesto, empresa, onNuevo, onVolver }) => {
  // Estado local en vez de mutar el prop directamente (mutar "presupuesto.estado"
  // no dispara un re-render en React) — así "Enviar" y "Aceptar" se reflejan en
  // pantalla al toque.
  const [estadoLocal, setEstadoLocal] = useState(presupuesto.estado);
  const [aceptadoCliente, setAceptadoCliente] = useState(!!presupuesto.aceptado_por_cliente);
  const [aceptando, setAceptando] = useState(false);

  const aceptarPresupuesto = async () => {
    setAceptando(true);
    try {
      await api.put(`/presupuestos/${presupuesto.id}/aceptar`, { token_acceso: presupuesto.token_acceso });
      setAceptadoCliente(true);
    } catch (err) {
      console.error(err);
      alert("No se pudo confirmar — probá de nuevo.");
    } finally {
      setAceptando(false);
    }
  };

  const generarPDF = () => {
    // Abrir PDF desde backend en lugar de generarlo en frontend
    window.open(`${API}/presupuestos/${presupuesto.id}/pdf?token_acceso=${encodeURIComponent(presupuesto.token_acceso || "")}`, "_blank");
  };

  const enviarWhatsApp = () => {
    try {
      let mensaje = `*🏠 AAGG REFORMAS - PRESUPUESTO ${presupuesto.numero || 'N/A'}*\n\n`;
      mensaje += `👤 Cliente: ${presupuesto.cliente_nombre || 'N/A'}\n`;
      mensaje += `📍 Dirección: ${presupuesto.cliente_direccion || 'N/A'}\n\n`;

      // Antes de "Confirmo, quiero este presupuesto" solo se manda el total
      // (mismo criterio que en pantalla) — el desglose por trabajo recién va
      // una vez que el cliente confirmó, no antes.
      const lineas = presupuesto.lineas || [];
      if (aceptadoCliente) {
        mensaje += `*📋 TRABAJOS:*\n`;
        lineas.forEach(l => {
          mensaje += `• ${l.trabajo_nombre || 'Trabajo'}: ${l.cantidad || 0} ${l.unidad || 'ud'} - ${(l.subtotal || 0).toFixed(2)}€\n`;
        });
        mensaje += `\n`;
      } else {
        mensaje += `*📋 TRABAJOS:* ${lineas.length}\n\n`;
      }

      mensaje += `💰 *TOTAL MANO DE OBRA: ${(presupuesto.total || 0).toFixed(2)}€*\n`;

      // Agregar materiales si existen
      const materiales = presupuesto.materiales_necesarios || [];
      if (materiales.length > 0) {
        const totalMateriales = materiales.reduce((sum, m) => sum + (m.subtotal || 0), 0);

        if (presupuesto.tipo_presupuesto === "mo") {
          mensaje += `\n\n*📦 MATERIALES QUE DEBES COMPRAR:*\n`;
          mensaje += `_(No incluido en el precio)_\n\n`;
        } else {
          mensaje += `\n\n*📦 MATERIALES INCLUIDOS:*\n\n`;
        }

        materiales.forEach(m => {
          mensaje += `• *${m.material_nombre || 'Material'}*\n`;
          const descripcion = m.descripcion_comercial || `${m.cantidad_calculada || m.cantidad || 0} ${m.material_unidad || 'ud'}`;
          mensaje += `  ${descripcion}\n`;
          if (m.marca) mensaje += `  Marca: ${m.marca}\n`;
          mensaje += `  ${(m.subtotal || 0).toFixed(2)}€\n\n`;
        });

        mensaje += `💵 Total Materiales: ${totalMateriales.toFixed(2)}€\n`;

        if (presupuesto.tipo_presupuesto === "mo_material") {
          const totalFinal = presupuesto.total_final || (presupuesto.total + totalMateriales);
          mensaje += `\n💰 *TOTAL FINAL (MO + Materiales): ${totalFinal.toFixed(2)}€*\n`;
        }
      }

      mensaje += `\n----\n${empresa?.nombre || "AAGG REFORMAS"}\n`;
      mensaje += `📞 ${empresa?.telefono || ""}\n`;
      mensaje += `📧 ${empresa?.email || ""}`;

      abrirWhatsApp(presupuesto.cliente_telefono, mensaje);

    } catch (error) {
      console.error("❌ Error enviando WhatsApp:", error);
      alert("❌ Error al enviar por WhatsApp: " + error.message);
    }
  };

  const temaPrototipo = presupuesto._temaColor
    ? { "--brand-primary": presupuesto._temaColorPrimary || presupuesto._temaColor, "--brand-accent": presupuesto._temaColor, "--brand-accent-light": presupuesto._temaColor }
    : undefined;

  return (
    <div className="pb-24" style={temaPrototipo}>
      <div className="container pt-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="card card-accent p-6 text-center mb-6"
        >
          <CheckCircle size={56} weight="fill" style={{ margin: "0 auto 12px" }} />
          <div className="text-h2 font-display">¡Presupuesto creado!</div>
          <div className="text-small" style={{ opacity: 0.8, marginTop: 4 }}>{presupuesto.numero}</div>
        </motion.div>

        <div className="card p-4 mb-4">
          <div className="flex justify-between mb-2">
            <span className="text-secondary">Cliente</span>
            <span className="font-bold">{presupuesto.cliente_nombre}</span>
          </div>
          <div className="flex justify-between mb-2">
            <span className="text-secondary">Trabajos</span>
            <span className="font-bold">{presupuesto.lineas.length}</span>
          </div>
          <div className="flex justify-between text-h3 font-display border-t border-subtle pt-3 mt-2">
            <span>Total Mano de Obra</span>
            <span style={{ color: "var(--brand-accent)" }}>{formatCurrency(presupuesto.total)}</span>
          </div>
        </div>

        {/* Fecha de inicio pedida — antes esto se guardaba pero el cliente
            nunca veía ninguna confirmación de que su pedido llegó. No hay
            forma de reabrir este presupuesto más tarde (no hay login), así
            que no se puede mostrar en vivo si Andy ya confirmó o repropuso
            — eso le llega por WhatsApp. Acá solo se confirma que el pedido
            se registró. */}
        {presupuesto.fecha_inicio_estimada && (
          <div className="card p-4 mb-4">
            <div className="flex items-center gap-2">
              <Calendar size={18} style={{ color: "var(--brand-accent)" }} />
              <div className="font-bold text-small">Fecha de inicio pedida</div>
            </div>
            <div className="text-small text-secondary mt-1">{formatDate(presupuesto.fecha_inicio_estimada)}</div>
            <div className="text-tiny text-secondary mt-2">Te confirmamos por WhatsApp si esa fecha va bien o si hay que ajustarla.</div>
          </div>
        )}

        {/* Desglose por trabajo — recién visible una vez confirmado. Antes de
            aceptar, solo se ve el total de arriba (no el precio por línea). */}
        {aceptadoCliente && (
          <div className="card p-4 mb-4">
            <label className="input-label">Detalle de trabajos</label>
            {presupuesto.lineas.map((linea, idx) => (
              <div key={idx} className="flex justify-between py-2" style={{ borderBottom: idx < presupuesto.lineas.length - 1 ? "1px solid var(--border-subtle)" : "none" }}>
                <div>
                  <div className="font-bold text-small">{linea.trabajo_nombre}</div>
                  <div className="text-tiny text-secondary">{linea.cantidad} {linea.unidad}</div>
                </div>
                <div className="font-bold">{formatCurrency(linea.subtotal)}</div>
              </div>
            ))}
          </div>
        )}

        {/* NUEVA SECCIÓN: Materiales necesarios */}
        {presupuesto.materiales_necesarios && presupuesto.materiales_necesarios.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card p-4 mb-4"
            style={{ background: "linear-gradient(135deg, color-mix(in srgb, var(--brand-accent) 10%, white) 0%, color-mix(in srgb, var(--brand-accent) 4%, white) 100%)", border: "2px solid var(--brand-accent)" }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Package size={24} weight="fill" style={{ color: "var(--brand-accent)" }} />
              <div className="text-h3 font-display" style={{ color: "color-mix(in srgb, var(--brand-accent) 55%, black)" }}>
                📋 Materiales que debes comprar
              </div>
            </div>

            <div className="text-small mb-3" style={{ color: "color-mix(in srgb, var(--brand-accent) 55%, black)", opacity: 0.9 }}>
              {presupuesto.tipo_presupuesto === "mo" 
                ? "Como elegiste 'Solo Mano de Obra', aquí está la lista de materiales que necesitarás comprar:"
                : "Materiales incluidos en este presupuesto:"}
            </div>

            <div className="space-y-3">
              {/* Agrupar por categoría */}
              {Object.entries(
                presupuesto.materiales_necesarios.reduce((acc, m) => {
                  const cat = m.material_categoria || "OTROS";
                  if (!acc[cat]) acc[cat] = [];
                  acc[cat].push(m);
                  return acc;
                }, {})
              ).map(([categoria, materiales]) => (
                <div key={categoria} className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="text-label mb-2" style={{ color: "color-mix(in srgb, var(--brand-accent) 55%, black)" }}>
                    {categoria}
                  </div>
                  {materiales.map((mat, idx) => (
                    <div key={idx} className="mb-2 pb-2 border-b border-subtle last:border-0 last:mb-0 last:pb-0">
                      <div className="flex justify-between items-start mb-1">
                        <div className="flex-1">
                          <div className="font-bold text-small">{mat.material_nombre}</div>
                          {mat.marca && (
                            <div className="text-tiny" style={{ color: "var(--text-secondary)" }}>
                              {mat.marca} {mat.referencia && `· Ref: ${mat.referencia}`}
                            </div>
                          )}
                        </div>
                        <div className="text-small font-bold" style={{ color: "var(--brand-accent)" }}>
                          {formatCurrency(mat.subtotal)}
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <div className="text-small" style={{ color: "var(--text-secondary)" }}>
                          {mat.descripcion_comercial || `${mat.cantidad_calculada || mat.cantidad} ${mat.material_unidad}`}
                        </div>
                        {mat.obligatorio === false && (
                          <span className="text-tiny" style={{
                            background: "color-mix(in srgb, var(--brand-accent) 15%, white)",
                            color: "color-mix(in srgb, var(--brand-accent) 55%, black)",
                            padding: "2px 6px",
                            borderRadius: "4px"
                          }}>
                            Opcional
                          </span>
                        )}
                      </div>

                      {mat.notas && (
                        <div className="text-tiny mt-1" style={{
                          color: "color-mix(in srgb, var(--brand-accent) 55%, black)",
                          background: "color-mix(in srgb, var(--brand-accent) 4%, white)",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          marginTop: "6px"
                        }}>
                          💡 {mat.notas}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
              
              {/* Total materiales */}
              <div className="flex justify-between items-center pt-3 border-t-2" style={{ borderColor: "var(--brand-accent)" }}>
                <span className="text-body font-bold" style={{ color: "color-mix(in srgb, var(--brand-accent) 55%, black)" }}>
                  Total Materiales
                </span>
                <span className="text-h3 font-display" style={{ color: "var(--brand-accent)" }}>
                  {formatCurrency(
                    presupuesto.materiales_necesarios.reduce((sum, m) => sum + m.subtotal, 0)
                  )}
                </span>
              </div>

              {presupuesto.tipo_presupuesto === "mo" && (
                <div className="text-tiny text-center pt-2" style={{ color: "color-mix(in srgb, var(--brand-accent) 55%, black)", opacity: 0.8 }}>
                  *Precios orientativos. Pueden variar según tienda y disponibilidad.
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Total completo si hay materiales Y tipo es mo_material */}
        {presupuesto.materiales_necesarios && presupuesto.materiales_necesarios.length > 0 && presupuesto.tipo_presupuesto === "mo_material" && (
          <div className="card p-4 mb-4" style={{ background: "var(--brand-primary)", color: "white" }}>
            <div className="flex justify-between items-center">
              <div>
                <div className="text-small" style={{ opacity: 0.9 }}>TOTAL A PAGAR</div>
                <div className="text-tiny" style={{ opacity: 0.7 }}>Mano de obra + Materiales incluidos</div>
              </div>
              <div className="text-hero font-display" style={{ color: "var(--brand-accent)" }}>
                {formatCurrency(presupuesto.total_final || (
                  presupuesto.total + 
                  presupuesto.materiales_necesarios.reduce((sum, m) => sum + m.subtotal, 0)
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {estadoLocal === "borrador" && (
            <Button
              variant="accent"
              className="btn-full"
              onClick={async () => {
                try {
                  await api.put(`/presupuestos/${presupuesto.id}/estado`, { estado: "pendiente", token_acceso: presupuesto.token_acceso });
                  alert("✅ Presupuesto enviado correctamente.\n\nTe contactaremos pronto para confirmar.");
                  setEstadoLocal("pendiente");
                } catch (err) {
                  alert("Error al enviar presupuesto");
                }
              }}
            >
              📨 Enviar presupuesto para revisión
            </Button>
          )}
          {estadoLocal === "pendiente" && !aceptadoCliente && (
            <>
              <div className="card p-3 text-center" style={{ background: "color-mix(in srgb, var(--brand-accent) 15%, white)", color: "color-mix(in srgb, var(--brand-accent) 55%, black)" }}>
                ⏳ Presupuesto en revisión. Te contactaremos pronto.
              </div>
              <Button variant="accent" className="btn-full" onClick={aceptarPresupuesto} disabled={aceptando}>
                {aceptando ? "Confirmando..." : "✅ Confirmo, quiero este presupuesto"}
              </Button>
            </>
          )}
          {aceptadoCliente && (
            <div className="card p-3 text-center" style={{ background: "color-mix(in srgb, var(--success) 15%, white)", color: "var(--success)" }}>
              ✅ ¡Confirmado! En breve nos ponemos en contacto para coordinar todo.
            </div>
          )}
          <Button variant="primary" className="btn-full" onClick={generarPDF}>
            <Download size={20} /> Descargar PDF
          </Button>
          <Button variant="whatsapp" className="btn-full" onClick={enviarWhatsApp}>
            <WhatsappLogo size={20} weight="fill" /> Enviar por WhatsApp
          </Button>
          <Button variant="outline" className="btn-full" onClick={onNuevo}>
            <Plus size={20} /> Crear otro presupuesto
          </Button>
          <Button
            variant="ghost"
            className="btn-full"
            onClick={onVolver}
          >
            ← Volver
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// APP CLIENTE
// ==========================================

const ClienteApp = () => {
  const [activeTab, setActiveTab] = useState("portada");
  const [empresa, setEmpresa] = useState(null);
  const [trabajos, setTrabajos] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [factores, setFactores] = useState([]);
  const [presupuestoCreado, setPresupuestoCreado] = useState(null);
  const [presupuestoParaEditar, setPresupuestoParaEditar] = useState(null);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [asesorAbierto, setAsesorAbierto] = useState(false);

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const [empRes, trabRes, modRes, facRes] = await Promise.all([
          api.get("/empresa"),
          api.get("/trabajos"),
          api.get("/modulos"),
          api.get("/factores"),
        ]);
        setEmpresa(empRes);
        setTrabajos(trabRes);
        setModulos(modRes);
        setFactores(facRes);
      } catch (e) {
        console.error("Error cargando datos:", e);
        setToast({ message: "Error cargando datos", type: "error" });
      } finally {
        setLoading(false);
      }
    };
    cargarDatos();
  }, []);

  const handlePresupuestoCreado = (presupuesto) => {
    setPresupuestoCreado(presupuesto);
    setToast({ message: "¡Presupuesto guardado!", type: "success" });
  };

  // Cualquier navegación genérica (portada, diagnóstico, barra inferior) que
  // lleve a "presupuesto" tiene que arrancar uno nuevo — si no, queda pegada
  // la pantalla de éxito o los datos del último presupuesto creado/editado.
  // La edición real de un presupuesto existente NO pasa por acá: pone
  // presupuestoParaEditar explícitamente antes de cambiar de tab.
  const navegarCliente = (tab) => {
    if (tab === "presupuesto") {
      setPresupuestoCreado(null);
      setPresupuestoParaEditar(null);
    }
    setActiveTab(tab);
  };

  if (loading) return <Loading />;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-base)" }}>
      {activeTab === "portada" && (
        <Portada
          empresa={empresa}
          modulos={modulos}
          onNavigate={navegarCliente}
          onAbrirAsesor={() => setAsesorAbierto(true)}
        />
      )}

      {activeTab === "tarjeta-digital" && <TarjetaDigital />}

      {activeTab === "diagnostico" && (
        <DiagnosticoInteligente
          trabajos={trabajos}
          modulos={modulos}
          onNavigate={navegarCliente}
          onGenerarPresupuesto={({ lineas, respuestas_diagnostico }) => {
            setPresupuestoParaEditar({
              cliente_nombre: "",
              cliente_telefono: "",
              cliente_email: "",
              cliente_direccion: "",
              calidad: "std",
              tipo_presupuesto: "mo_material",
              incluye_iva: true,
              lineas,
              factores_aplicados: [],
              descuento_porcentaje: 0,
              descripcion_cliente: "",
              respuestas_diagnostico: respuestas_diagnostico || {},
            });
            setActiveTab("presupuesto");
          }}
        />
      )}

      {activeTab === "catalogo" && <CatalogoCliente />}

      {activeTab === "presupuesto" && !presupuestoCreado && (
        <CrearPresupuesto
          trabajos={trabajos}
          modulos={modulos}
          factores={factores}
          empresa={empresa}
          onCrear={handlePresupuestoCreado}
          onBack={() => {
            setPresupuestoParaEditar(null);
            setActiveTab("portada");
          }}
          presupuestoInicial={presupuestoParaEditar}
        />
      )}

      {activeTab === "presupuesto" && presupuestoCreado && (
        <ResumenPresupuestoCreado
          presupuesto={presupuestoCreado}
          empresa={empresa}
          onNuevo={() => {
            setPresupuestoParaEditar(null);
            setPresupuestoCreado(null);
          }}
          onVolver={() => {
            setPresupuestoParaEditar(presupuestoCreado);
            setPresupuestoCreado(null);
          }}
        />
      )}

      <BottomNav
        activeTab={activeTab}
        onNavigate={navegarCliente}
        isAdmin={false}
      />

      {/* Asesor IA Modal */}
      <AsesorTecnicoIA 
        isOpen={asesorAbierto} 
        onClose={() => setAsesorAbierto(false)} 
      />

      <AnimatePresence>
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ==========================================
// APP ADMIN (Simplificada por ahora)
// ==========================================

const AdminApp = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [token, setToken] = useState(localStorage.getItem("admin_token"));
  const [pin, setPin] = useState("");
  const [activeTab, setActiveTab] = useState("dashboard");
  const [presupuestoParaEditar, setPresupuestoParaEditar] = useState(null);
  // "Nuevo Presupuesto" del menú Admin: Andy arma el presupuesto él mismo,
  // en la casa del cliente, sin pedir nombre/teléfono hasta el final (ver
  // prop modoOperario en CrearPresupuesto). Se resetea a false en cualquier
  // otro camino hacia "presupuesto" (editar un pendiente, tab normal) para
  // que no quede pegado.
  const [modoOperario, setModoOperario] = useState(false);
  // "Modo prueba" del menú Admin (aparte de "Nuevo Presupuesto", que no se
  // toca): reusa el mismo flujo relajado de modoOperario pero precarga
  // cliente="PRUEBAS DESDE APP" para que Andy pueda verificar que cada
  // módulo dé bien precio/materiales/horas sin escribir nada y sin que se
  // confunda con un lead real.
  const [modoPrueba, setModoPrueba] = useState(false);
  const [presupuestoCreado, setPresupuestoCreado] = useState(null);
  const [stats, setStats] = useState({});
  const [presupuestos, setPresupuestos] = useState([]);
  const [trabajos, setTrabajos] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [factores, setFactores] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminConfigured, setAdminConfigured] = useState(null);
  const [setupData, setSetupData] = useState({ pin: "", pregunta: "", respuesta: "" });

  useEffect(() => {
    checkAdmin();
  }, []);

  const checkAdmin = async () => {
    api.get("/empresa").then(setEmpresa).catch(() => {});
    try {
      const res = await api.get("/admin/check");
      setAdminConfigured(res.configured);
      if (res.configured && token) {
        setAuthenticated(true);
        cargarDatos();
      } else {
        setLoading(false);
      }
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  const cargarDatos = async (authToken = null) => {
    const currentToken = authToken || token || localStorage.getItem("admin_token");
    try {
      const [statsRes, presRes, trabRes, modRes, facRes, empRes] = await Promise.all([
        api.get("/admin/stats", currentToken).catch(() => ({})),
        api.get("/presupuestos", currentToken),
        api.get("/trabajos"),
        api.get("/modulos"),
        api.get("/factores"),
        api.get("/empresa"),
      ]);
      setStats(statsRes);
      setPresupuestos(presRes);
      setTrabajos(trabRes);
      setModulos(modRes);
      setFactores(facRes);
      setEmpresa(empRes);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async () => {
    if (setupData.pin.length !== 4) {
      setToast({ message: "El PIN debe tener 4 dígitos", type: "error" });
      return;
    }
    try {
      const res = await api.post("/admin/setup", {
        pin: setupData.pin,
        pregunta_secreta: setupData.pregunta,
        respuesta_secreta: setupData.respuesta,
      });
      localStorage.setItem("admin_token", res.token);
      setToken(res.token);
      setAuthenticated(true);
      setAdminConfigured(true);
      cargarDatos(res.token);
      setToast({ message: "Admin configurado", type: "success" });
    } catch (e) {
      setToast({ message: "Error configurando", type: "error" });
    }
  };

  const handleLogin = async (pinValue) => {
    if (pinValue.length !== 4) return;
    try {
      const res = await api.post("/admin/login", { pin: pinValue });
      localStorage.setItem("admin_token", res.token);
      setToken(res.token);
      setAuthenticated(true);
      cargarDatos(res.token);
    } catch (e) {
      setToast({ message: "PIN incorrecto", type: "error" });
      setPin("");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
    setAuthenticated(false);
  };

  // PIN Pad
  const PinPad = () => (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(135deg, var(--brand-primary), var(--brand-secondary))" }}
    >
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          {empresa?.logo_url ? (
            <img
              src={empresa.logo_url}
              alt={empresa?.nombre || "AAGG Reformas"}
              style={{ maxWidth: 170, width: "100%", height: "auto", margin: "0 auto 16px", display: "block" }}
            />
          ) : (
            <Lock size={56} weight="duotone" style={{ color: "var(--brand-accent)", margin: "0 auto 16px" }} />
          )}
          <div className="text-h2 font-display text-white">Panel Admin</div>
          <div className="text-small text-white" style={{ opacity: 0.6 }}>{empresa?.nombre || "Andy Gonzaga"}</div>
        </div>

        <div className="card p-6">
          <div className="text-center font-bold mb-2">Introduce tu PIN</div>
          <div className="text-center text-tiny text-secondary mb-6">4 dígitos</div>

          <div className="pin-dots mb-6">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={`pin-dot ${pin.length > i ? "pin-dot-filled" : ""}`}
              />
            ))}
          </div>

          <div className="pin-pad">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, "C", 0, "⌫"].map((key) => (
              <button
                key={key}
                onClick={() => {
                  if (key === "C") setPin("");
                  else if (key === "⌫") setPin(pin.slice(0, -1));
                  else if (pin.length < 4) {
                    const newPin = pin + key;
                    setPin(newPin);
                    if (newPin.length === 4) {
                      setTimeout(() => handleLogin(newPin), 200);
                    }
                  }
                }}
                className="pin-key"
              >
                {key}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Setup
  const SetupScreen = () => (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(135deg, var(--brand-primary), var(--brand-secondary))" }}
    >
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Gear size={56} weight="duotone" style={{ color: "var(--brand-accent)", margin: "0 auto 16px" }} />
          <div className="text-h2 font-display text-white">Configurar Admin</div>
        </div>

        <div className="card p-6">
          <Input
            label="PIN (4 dígitos)"
            type="password"
            maxLength={4}
            value={setupData.pin}
            onChange={(e) => setSetupData({ ...setupData, pin: e.target.value })}
            placeholder="****"
          />
          <Input
            label="Pregunta secreta"
            value={setupData.pregunta}
            onChange={(e) => setSetupData({ ...setupData, pregunta: e.target.value })}
            placeholder="¿Tu pregunta?"
          />
          <Input
            label="Respuesta"
            value={setupData.respuesta}
            onChange={(e) => setSetupData({ ...setupData, respuesta: e.target.value })}
            placeholder="Tu respuesta"
          />
          <Button variant="accent" className="btn-full mt-4" onClick={handleSetup}>
            Configurar
          </Button>
        </div>
      </div>
    </div>
  );

  // Dashboard
  const Dashboard = () => (
    <div className="pb-24">
      <div className="hero-header">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            {empresa?.logo_url && (
              <img
                src={empresa.logo_url}
                alt={empresa?.nombre || "AAGG Reformas"}
                style={{ height: 42, width: "auto", background: "white", borderRadius: 8, padding: "5px 9px", boxShadow: "0 4px 14px rgba(0,0,0,0.35)" }}
              />
            )}
            <div>
              <div className="text-tiny" style={{ opacity: 0.6 }}>Panel Admin</div>
              <div className="text-h2 font-display">{empresa?.nombre || "Andy Gonzaga"}</div>
            </div>
          </div>
          <button onClick={handleLogout} className="btn btn-sm btn-outline" style={{ borderColor: "rgba(255,255,255,0.3)", color: "white" }}>
            Salir
          </button>
        </div>
      </div>

      <div className="container" style={{ marginTop: -20, position: "relative", zIndex: 2 }}>
        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="card card-elevated stat-card">
            <div className="stat-value">{stats.total_presupuestos || 0}</div>
            <div className="stat-label">Presupuestos</div>
          </div>
          <div className="card stat-card" style={{ background: "var(--success)", color: "white" }}>
            <div className="stat-value">{formatCurrency(stats.facturado_mes)}</div>
            <div className="stat-label" style={{ color: "rgba(255,255,255,0.8)" }}>Este mes</div>
          </div>
          <div className="card card-elevated stat-card">
            <div className="stat-value">{stats.pendientes || 0}</div>
            <div className="stat-label">Pendientes</div>
          </div>
          <div className="card card-elevated stat-card">
            <div className="stat-value">{stats.finalizados || 0}</div>
            <div className="stat-label">Finalizados</div>
          </div>
        </div>

        {/* Tasa cierre */}
        <div className="card p-4 mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="font-bold">Tasa de cierre</span>
            <span className="text-h3 font-display">{stats.tasa_cierre || 0}%</span>
          </div>
          <div className="progress-step" style={{ height: 8 }}>
            <div className="progress-step-fill" style={{ width: `${stats.tasa_cierre || 0}%` }} />
          </div>
        </div>

        {/* Últimos presupuestos */}
        <div className="section">
          <h3 className="section-title">
            <FileText size={20} /> Últimos presupuestos
          </h3>
          <div className="card">
            {presupuestos.slice(0, 5).map((p) => (
              <div
                key={p.id}
                className="list-item list-item-interactive"
                onClick={() => setActiveTab(p.estado === "pendiente" ? "presupuestos-pendientes" : "historial-completo")}
              >
                <div className="list-item-content">
                  <div className="list-item-title">{p.cliente_nombre}</div>
                  <div className="list-item-subtitle">{p.numero} · {formatDate(p.fecha)}</div>
                </div>
                <div className="text-right">
                  <div className="font-bold">{formatCurrency(p.total)}</div>
                  <Badge variant={p.estado === "finalizado" ? "success" : p.estado === "aceptado" ? "primary" : "outline"}>
                    {p.estado}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) return <Loading />;
  if (adminConfigured === false) return <SetupScreen />;
  if (!authenticated) return (
    <>
      <PinPad />
      <AnimatePresence>
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </AnimatePresence>
    </>
  );

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-base)" }}>
      {activeTab === "dashboard" && <Dashboard />}
      
      {activeTab === "gestora" && <GestoraAutomatica onNavigate={setActiveTab} adminToken={token} />}
      
      {/* Modo operario: al guardar, en vez de navegar directo a "historial"
          se muestra la misma pantalla de éxito que ve el cliente (con el
          botón real de "Enviar por WhatsApp", que ya arma el link al
          teléfono real recién cargado) — así Andy puede mandárselo al
          cliente ahí mismo, sin salir de la app. */}
      {activeTab === "presupuesto" && !presupuestoCreado && (
        <CrearPresupuesto
          trabajos={trabajos}
          modulos={modulos}
          factores={factores}
          empresa={empresa}
          presupuestoInicial={presupuestoParaEditar}
          modoOperario={modoOperario}
          modoPrueba={modoPrueba}
          adminToken={token}
          onCrear={(p) => {
            if (modoOperario) {
              setPresupuestoCreado(p);
              cargarDatos();
              return;
            }
            setToast({ message: presupuestoParaEditar ? "Presupuesto actualizado" : "Presupuesto creado", type: "success" });
            setActiveTab(presupuestoParaEditar ? "presupuestos-pendientes" : "historial");
            setPresupuestoParaEditar(null);
            cargarDatos();
          }}
        />
      )}

      {activeTab === "presupuesto" && presupuestoCreado && (
        <ResumenPresupuestoCreado
          presupuesto={presupuestoCreado}
          empresa={empresa}
          onNuevo={() => {
            setModoOperario(true);
            setPresupuestoParaEditar(null);
            setPresupuestoCreado(null);
          }}
          onVolver={() => {
            setModoOperario(false);
            setModoPrueba(false);
            setPresupuestoParaEditar(null);
            setPresupuestoCreado(null);
            setActiveTab("admin");
          }}
        />
      )}

      {activeTab === "historial" && (
        <div className="pb-24">
          <div className="container pt-4">
            <h1 className="text-h2 font-display mb-4">
              <FileText size={28} style={{ display: "inline", marginRight: 8 }} />
              Historial
            </h1>
            <div className="card">
              {presupuestos.map((p) => (
                <div key={p.id} className="list-item">
                  <div className="list-item-content">
                    <div className="list-item-title">{p.cliente_nombre}</div>
                    <div className="list-item-subtitle">{p.numero} · {p.lineas?.length || 0} trabajos</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">{formatCurrency(p.total)}</div>
                    <Badge variant={p.estado === "finalizado" ? "success" : "outline"}>{p.estado}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === "precios" && (
        <ListaPrecios trabajos={trabajos} modulos={modulos} isAdmin={true} />
      )}

      {activeTab === "admin" && (
        <MenuAdmin
          onNavigate={(id) => {
            if (id === "presupuesto-nuevo") {
              setModoOperario(true);
              setModoPrueba(false);
              setPresupuestoParaEditar(null);
              setPresupuestoCreado(null);
              setActiveTab("presupuesto");
              return;
            }
            if (id === "presupuesto-prueba") {
              setModoOperario(true);
              setModoPrueba(true);
              setPresupuestoParaEditar(null);
              setPresupuestoCreado(null);
              setActiveTab("presupuesto");
              return;
            }
            setActiveTab(id);
          }}
        />
      )}

      {activeTab === "asistente-obra" && <AsistenteObra adminToken={token} />}
      {activeTab === "editor-trabajos" && <EditorTrabajos adminToken={token} />}
      {activeTab === "editor-preguntas" && <EditorPreguntas adminToken={token} />}
      {activeTab === "presupuestos-pendientes" && (
        <PresupuestosPendientes
          adminToken={token}
          onEditar={(p) => {
            setPresupuestoParaEditar(p);
            setModoOperario(false);
            setModoPrueba(false);
            setPresupuestoCreado(null);
            setActiveTab("presupuesto");
          }}
        />
      )}
      {activeTab === "materiales" && <GestorMateriales adminToken={token} />}
      {activeTab === "proveedores" && <Proveedores adminToken={token} />}
      {activeTab === "agenda" && <Agenda adminToken={token} />}
      {activeTab === "visitas" && <VisitaTecnica adminToken={token} />}
      {activeTab === "facturas" && <Facturas adminToken={token} />}
      {activeTab === "formulas" && <Formulas adminToken={token} />}
      {activeTab === "garantias" && <Garantias adminToken={token} />}
      {activeTab === "crm" && <CRMClientes adminToken={token} />}
      {activeTab === "whatsapp-inbox" && <WhatsAppInbox adminToken={token} />}
      {activeTab === "catalogo-admin" && <Catalogo isAdmin={true} adminToken={token} />}


      {activeTab === "configuracion" && <Configuracion adminToken={token} />}
      {activeTab === "historial-completo" && <Historial adminToken={token} />}
      {activeTab === "informes" && <Informes adminToken={token} />}
      {activeTab === "notificaciones" && <Notificaciones adminToken={token} onNavigate={setActiveTab} />}

      <BottomNav
        activeTab={activeTab}
        onNavigate={(tab) => {
          if (tab === "presupuesto") {
            setPresupuestoParaEditar(null);
            setModoOperario(false);
            setModoPrueba(false);
            setPresupuestoCreado(null);
          }
          setActiveTab(tab);
        }}
        isAdmin={true}
      />

      <AnimatePresence>
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </AnimatePresence>
    </div>
  );
};

// ==========================================
// ROUTER
// ==========================================

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<ClienteApp />} />
        <Route path="/admin" element={<AdminApp />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
